<script>
import { defineAsyncComponent, useTemplateRef } from 'vue';
import { resolveAceleresApiBase } from 'shared/helpers/aceleresApiBase';
import { useMediaQuery } from '@vueuse/core';
import { Dropdown, recomputeAllPoppers } from 'floating-vue';
import { mapGetters } from 'vuex';
import { useAlert } from 'dashboard/composables';
import { useUISettings } from 'dashboard/composables/useUISettings';
import { useTrack } from 'dashboard/composables';
import keyboardEventListenerMixins from 'shared/mixins/keyboardEventListenerMixins';
import { FEATURE_FLAGS } from 'dashboard/featureFlags';

import ReplyToMessage from './ReplyToMessage.vue';
import MobileEmojiSheet from 'dashboard/components-next/emoji/MobileEmojiSheet.vue';
import AttachmentPreview from 'dashboard/components/widgets/AttachmentsPreview.vue';
import ReplyTopPanel from 'dashboard/components/widgets/WootWriter/ReplyTopPanel.vue';
import ReplyEmailHead from './ReplyEmailHead.vue';
import ReplyBottomPanel from 'dashboard/components/widgets/WootWriter/ReplyBottomPanel.vue';
import CopilotReplyBottomPanel from 'dashboard/components/widgets/WootWriter/CopilotReplyBottomPanel.vue';
import AceleresTeamPresenceAPI from 'dashboard/api/aceleresTeamPresence';
import AceleresCannedPopover from 'dashboard/components/widgets/WootWriter/AceleresCannedPopover.vue';
import SendContactModal from 'dashboard/modules/conversations/components/SendContactModal.vue';
import ArticleSearchPopover from 'dashboard/routes/dashboard/helpcenter/components/ArticleSearch/SearchPopover.vue';
import CopilotEditorSection from './CopilotEditorSection.vue';
import ReplyBoxBanner from './ReplyBoxBanner.vue';
import AceleresWhatsappDisconnectedPanel from './AceleresWhatsappDisconnectedPanel.vue';
import QuotedEmailPreview from './QuotedEmailPreview.vue';
import { REPLY_EDITOR_MODES } from 'dashboard/components/widgets/WootWriter/constants';
import { buildDraftKey } from 'dashboard/helper/draftMessageHelper';
import { looksLikeGif } from 'dashboard/helper/gifMediaHelper';
import { INBOX_TYPES } from 'dashboard/helper/inbox';
import { shouldShowMetaMessagingWindowExpiredNotice } from 'dashboard/helper/metaMessagingWindow';
import { renderLottieStickerFrame } from 'dashboard/helper/lottieStickerPlayer';
import {
  isAudioAttachment,
  normalizeUploadedAudioFile,
} from 'dashboard/helper/audioAttachmentHelper';
import WootMessageEditor from 'dashboard/components/widgets/WootWriter/Editor.vue';
import AudioRecorder from 'dashboard/components/widgets/WootWriter/AudioRecorder.vue';
import { AUDIO_FORMATS, MESSAGE_STATUS } from 'shared/constants/messages';
import { BUS_EVENTS } from 'shared/constants/busEvents';
import { CMD_AI_ASSIST } from 'dashboard/helper/commandbar/events';
import {
  getMessageVariables,
  getUndefinedVariablesInMessage,
} from '@chatwoot/utils';
import WhatsappTemplates from './WhatsappTemplates/Modal.vue';
import ContentTemplates from './ContentTemplates/ContentTemplatesModal.vue';
import { MESSAGE_MAX_LENGTH } from 'shared/helpers/MessageTypeHelper';
import inboxMixin, { INBOX_FEATURES } from 'shared/mixins/inboxMixin';
import { trimContent, debounce, getRecipients } from '@chatwoot/utils';
import wootConstants from 'dashboard/constants/globals';
import {
  extractQuotedEmailText,
  buildQuotedEmailHeader,
  truncatePreviewText,
  appendQuotedTextToMessage,
} from 'dashboard/helper/quotedEmailHelper';
import {
  CONVERSATION_EVENTS,
  CAPTAIN_EVENTS,
} from '../../../helper/AnalyticsHelper/events';
import fileUploadMixin from 'dashboard/mixins/fileUploadMixin';
import {
  appendSignature,
  removeSignature,
  getEffectiveChannelType,
} from 'dashboard/helper/editorHelper';
import { useCopilotReply } from 'dashboard/composables/useCopilotReply';
import { useKbd } from 'dashboard/composables/utils/useKbd';
import { isFileTypeAllowedForChannel } from 'shared/helpers/FileHelper';
import {
  createPdfFirstPagePreview,
  isPdfFile,
} from 'dashboard/helper/pdfPreviewHelper';
import {
  aceleresNeedsStartGate,
  isAceleresConversationStarted,
  isAceleresPersonalConversation,
} from 'dashboard/helper/aceleresConversationState';
import { isTrainingConversation } from 'dashboard/helper/trainingConversation';

import { LOCAL_STORAGE_KEYS } from 'dashboard/constants/localStorage';
import { LocalStorage } from 'shared/helpers/localStorage';
import { emitter } from 'shared/helpers/mitt';

const loadEmojiInput = () => import('shared/components/emoji/EmojiInput.vue');
const EmojiInput = defineAsyncComponent(loadEmojiInput);

// Aceleres: limites do arrasto da folha de emojis no mobile. Arrastar o
// puxador só move a folha; fechar exige levá-la até o fim da tela. Por isso o
// gesto pode encolhê-la até zero, mas só soltar abaixo de DISMISS (encostada
// no rodapé) fecha — soltar em qualquer altura intermediária devolve a folha
// para MIN, que é a menor altura em que ela ainda serve para alguma coisa.
// O padrao do snackbar (2,5s) sumia antes de o vendedor ler o motivo da falha.
const SEND_ERROR_ALERT_DURATION = 7000;
const EMOJI_SHEET_MIN_HEIGHT = 200;
const EMOJI_SHEET_DISMISS_HEIGHT = 56;

export default {
  components: {
    ArticleSearchPopover,
    MobileEmojiSheet,
    AttachmentPreview,
    AudioRecorder,
    ReplyBoxBanner,
    AceleresWhatsappDisconnectedPanel,
    EmojiInput,
    ReplyBottomPanel,
    ReplyEmailHead,
    ReplyToMessage,
    ReplyTopPanel,
    ContentTemplates,
    WhatsappTemplates,
    WootMessageEditor,
    QuotedEmailPreview,
    CopilotEditorSection,
    CopilotReplyBottomPanel,
    AceleresCannedPopover,
    SendContactModal,
    Dropdown,
  },
  mixins: [inboxMixin, fileUploadMixin, keyboardEventListenerMixins],
  emits: ['toggleEditorSize', 'attachmentProcessingChange'],
  setup() {
    const {
      uiSettings,
      isEditorHotKeyEnabled,
      fetchSignatureFlagFromUISettings,
      setQuotedReplyFlagForInbox,
      fetchQuotedReplyFlagFromUISettings,
    } = useUISettings();

    const replyEditor = useTemplateRef('replyEditor');
    const messageEditor = useTemplateRef('messageEditor');
    const isMobileViewport = useMediaQuery('(max-width: 767px)');
    const isNarrowMobileViewport = useMediaQuery('(max-width: 360px)');
    const isCopilotMobileViewport = useMediaQuery('(max-width: 768px)');
    const copilot = useCopilotReply();
    const shortcutKey = useKbd(['$mod', '+', 'enter']);

    return {
      uiSettings,
      isEditorHotKeyEnabled,
      fetchSignatureFlagFromUISettings,
      setQuotedReplyFlagForInbox,
      fetchQuotedReplyFlagFromUISettings,
      replyEditor,
      messageEditor,
      isMobileViewport,
      isNarrowMobileViewport,
      isCopilotMobileViewport,
      copilot,
      shortcutKey,
    };
  },
  data() {
    return {
      aceleresStarting: false,
      // Relógio grosso (1 min) só para a janela de mensageria envelhecer com a
      // aba aberta. Ver `isOutsideMessagingWindow`.
      aceleresNowTs: Math.floor(Date.now() / 1000),
      message: '',
      inReplyTo: {},
      isFocused: false,
      suppressEditorFocusOnMount: false,
      showEmojiPicker: false,
      // Aceleres 2026-09-15: pedido do "+" das reacoes rapidas no telemovel
      // (`{ messageId, onSelect }`). Preenchido, abre a folha grande por cima
      // de tudo (MobileEmojiSheet); o compositor nao e tocado.
      reactionSheetRequest: null,
      // A galeria de mídia é derivada dos anexos. Enquanto o picker de
      // figurinhas estiver aberto, ela fica temporariamente suprimida para
      // que os dois painéis nunca ocupem o composer ao mesmo tempo.
      suppressWhatsAppGallery: false,
      // Altura da folha de emojis no mobile quando o agente a arrasta. Zero
      // significa "usa a altura padrão do CSS".
      emojiSheetHeight: 0,
      emojiSheetDragging: false,
      emojiSheetDragMoved: false,
      emojiSheetDragStartY: 0,
      emojiSheetDragStartHeight: 0,
      attachedFiles: [],
      pendingAttachmentUploads: 0,
      isRecordingAudio: false,
      recordingAudioState: '',
      isRecordingPaused: false,
      recordingAudioDurationText: '',
      // Aceleres: true quando o usuário clicou em Enviar durante a gravação
      // (WhatsApp). O gatilho do envio vive no attachFile (não num watcher —
      // em Vue 3 watcher de array reativo não dispara no push). Ver
      // sendAudioRecording + attachFile.
      sendOnFinish: false,
      replyType: REPLY_EDITOR_MODES.REPLY,
      // Ver watcher de replyType.
      skipReplyTypeDraftSync: false,
      bccEmails: '',
      ccEmails: '',
      toEmails: '',
      doAutoSaveDraft: () => {},
      showWhatsAppTemplatesModal: false,
      showContentTemplatesModal: false,
      updateEditorSelectionWith: '',
      undefinedVariableMessage: '',
      showMentions: false,
      showUserMentions: false,
      showCannedMenu: false,
      showAceleresCannedPopover: false,
      showSendContactModal: false,
      showVariablesMenu: false,
      newConversationModalActive: false,
      showArticleSearchPopover: false,
      hasRecordedAudio: false,
      copilotAcceptedMessages: {},
      isReplyBoxMultiline: false,
      replyBoxResizeObserver: null,
      replyBoxResizeFrame: null,
      replyBoxKeydownCapture: null,
      isSendingAttachments: false,
      isSendingGif: false,
      attachmentSendProgress: { current: 0, total: 0, active: 0 },
      inboxConnectionVerifiedId: null,
      inboxConnectionVerifiedConversationId: null,
      inboxConnectionVerificationFailedId: null,
      inboxConnectionVerificationFailedConversationId: null,
      verifiedInbox: null,
      isConversationFileDragActive: false,
    };
  },
  computed: {
    ...mapGetters({
      currentChat: 'getSelectedChat',
      messageSignature: 'getMessageSignature',
      currentUser: 'getCurrentUser',
      lastEmail: 'getLastEmailInSelectedChat',
      globalConfig: 'globalConfig/get',
      accountId: 'getCurrentAccountId',
      isFeatureEnabledonAccount: 'accounts/isFeatureEnabledonAccount',
    }),
    isCurrentAccountSeller() {
      const currentAccount = this.currentUser?.accounts?.find(
        account => Number(account.id) === Number(this.accountId)
      );

      return currentAccount?.role === 'agent';
    },
    currentContact() {
      const senderId = this.currentChat?.meta?.sender?.id;
      if (!senderId) return {};
      return this.$store.getters['contacts/getContact'](senderId);
    },
    // Aceleres: o card só vira um bloco só com a pílula quando é o vizinho
    // imediato dela. Com anexos ou citação de e-mail no meio, esses banners
    // entram entre os dois e o card volta a ser um cartão solto.
    replyCardAttached() {
      return (
        this.shouldShowReplyToMessage &&
        !this.hasAttachments &&
        !(this.shouldShowQuotedPreview && this.isDefaultEditorMode)
      );
    },
    shouldShowReplyToMessage() {
      return (
        this.inReplyTo?.id &&
        !this.showWhatsAppGallery &&
        !this.hasAttachments &&
        (!this.isPrivate || this.isMobileViewport) &&
        this.inboxHasFeature(INBOX_FEATURES.REPLY_TO) &&
        !this.is360DialogWhatsAppChannel &&
        !this.copilot.isActive.value
      );
    },
    showWhatsappTemplates() {
      // We support templates for API channels if someone updates templates manually via API
      // That's why we don't explicitly check for channel type here
      const templates = this.$store.getters['inboxes/getWhatsAppTemplates'](
        this.inboxId
      );
      return !!(templates && templates.length) && !this.isPrivate;
    },
    showContentTemplates() {
      return this.isATwilioWhatsAppChannel && !this.isPrivate;
    },
    // Aceleres: nota interna e SEMPRE o modo escolhido pelo agente. O
    // upstream devolvia `true` aqui quando `can_reply` era falso e o canal
    // nao era WhatsApp/API -- o compositor virava nota sozinho (fundo ambar,
    // placeholder de nota) e, pior, `private: true` ia no envio. Como
    // `can_reply` chega falso para toda conversa fora da janela de 24h e o
    // canal so e reconhecido depois que a inbox entra na store, isso disparava
    // ao trocar de conversa e ate parado na mesma conversa. A trava de janela
    // continua onde deve estar: `isEditorDisabled` + painel de sessao expirada.
    isPrivate() {
      return this.isOnPrivateNote;
    },
    isTrainingConversationContext() {
      return isTrainingConversation(this.currentChat, this.currentUser);
    },
    inboxId() {
      return this.currentChat.inbox_id;
    },
    inbox() {
      const cachedInbox = this.$store.getters['inboxes/getInbox'](this.inboxId);
      if (this.verifiedInbox?.id === Number(this.inboxId)) {
        return this.verifiedInbox;
      }
      if (this.currentChat?.inbox_connection_status === undefined) {
        return cachedInbox;
      }
      return {
        ...cachedInbox,
        additional_attributes: {
          ...cachedInbox.additional_attributes,
          connection_status: this.currentChat.inbox_connection_status,
          aceleres_instance_id:
            this.currentChat.inbox_instance_id ||
            cachedInbox.additional_attributes?.aceleres_instance_id,
        },
      };
    },
    isWameInbox() {
      return (
        Number(this.inbox?.additional_attributes?.aceleres_instance_id || 0) > 0
      );
    },
    isApiInboxConnectionPending() {
      const inboxId = Number(this.inboxId);
      const isAPIInbox =
        this.currentChat?.meta?.channel === 'Channel::Api' || this.isAPIInbox;
      return (
        isAPIInbox &&
        inboxId > 0 &&
        (this.inboxConnectionVerifiedId !== inboxId ||
          this.inboxConnectionVerifiedConversationId !==
            Number(this.currentChat?.id)) &&
        !this.isInboxConnectionVerificationFailed
      );
    },
    isInboxConnectionVerificationFailed() {
      const isAPIInbox =
        this.currentChat?.meta?.channel === 'Channel::Api' || this.isAPIInbox;
      return (
        isAPIInbox &&
        Number(this.inboxId) > 0 &&
        this.inboxConnectionVerificationFailedId === Number(this.inboxId) &&
        this.inboxConnectionVerificationFailedConversationId ===
          Number(this.currentChat?.id)
      );
    },
    isApiInboxConnectionBlocked() {
      return (
        this.isApiInboxConnectionPending ||
        this.isInboxConnectionVerificationFailed
      );
    },
    whatsappInstanceConnecting() {
      const status = String(
        this.inbox?.additional_attributes?.connection_status || ''
      ).toLowerCase();
      return (
        (this.currentChat?.meta?.channel === 'Channel::Api' ||
          this.isAPIInbox) &&
        status === 'connecting'
      );
    },
    whatsappInstanceUnavailable() {
      return (
        this.whatsappInstanceConnecting ||
        this.whatsappInstanceDisconnected ||
        this.whatsappInstanceUnconfirmed
      );
    },
    whatsappInstanceUnconfirmed() {
      return (
        this.isWameInbox &&
        this.inboxConnectionVerifiedId === Number(this.inboxId) &&
        this.inboxConnectionVerifiedConversationId ===
          Number(this.currentChat?.id) &&
        !this.whatsappInstanceDisconnected &&
        !this.whatsappInstanceConnecting &&
        String(
          this.inbox?.additional_attributes?.connection_status || ''
        ).toLowerCase() !== 'connected'
      );
    },
    isWameAPIInbox() {
      return (
        this.isAPIInbox &&
        this.inbox?.additional_attributes?.aceleres_outbound_mode === 'webhook'
      );
    },
    isFunilDesktopViewport() {
      return (
        window.frameElement?.id === 'funilChatFrame' &&
        !window.top.matchMedia('(max-width: 767px)').matches
      );
    },
    messagePlaceHolder() {
      if (this.isEditorDisabled) {
        if (this.currentContact?.blocked ?? this.currentChat?.muted) {
          return this.$t('CONVERSATION.FOOTER.MESSAGING_RESTRICTED_BLOCKED_CONTACT');
        }
        if (this.whatsappInstanceDisconnected) {
          return 'Atendimento pausado. Reconecte a instância antes de enviar.';
        }
        if (this.isApiInboxConnectionPending) {
          return 'Aguarde a verificação da conexão da instância.';
        }
        if (this.isInboxConnectionVerificationFailed) {
          return 'Não foi possível verificar a conexão. Tente novamente antes de enviar.';
        }
        if (this.whatsappInstanceConnecting) {
          return 'Aguarde a conexão da instância antes de enviar.';
        }
        if (this.isMetaMessagingWindowExpired) {
          return this.$t('CONVERSATION.FOOTER.MESSAGING_RESTRICTED_SOCIAL');
        }
        if (this.isAWhatsAppChannel) {
          return this.$t('CONVERSATION.FOOTER.MESSAGING_RESTRICTED_WHATSAPP');
        }
        if (this.isAPIInbox) {
          return this.$t('CONVERSATION.FOOTER.MESSAGING_RESTRICTED_API');
        }
        return this.$t('CONVERSATION.FOOTER.MESSAGING_RESTRICTED');
      }
      if (
        this.isMobileViewport &&
        !this.isFunilDesktopViewport &&
        !this.isPrivate
      ) {
        return this.isNarrowMobileViewport ? 'Mensagem' : 'Digite uma mensagem';
      }
      return this.isPrivate
        ? this.$t('CONVERSATION.FOOTER.PRIVATE_MSG_INPUT')
        : this.$t('CONVERSATION.FOOTER.MSG_INPUT');
    },
    isMessageLengthReachingThreshold() {
      return this.message.length > this.maxLength - 50;
    },
    charactersRemaining() {
      return this.maxLength - this.message.length;
    },
    isReplyButtonDisabled() {
      if (this.isSendingAttachments) return true;
      if (this.isEditorDisabled) return true;
      if (this.isATwitterInbox) return true;
      if (this.hasAttachments || this.hasRecordedAudio) return false;

      return this.isMessageEmpty || this.message.length > this.maxLength;
    },
    sender() {
      return {
        name: this.currentUser.name,
        thumbnail: this.currentUser.avatar_url,
      };
    },
    conversationType() {
      const { additional_attributes: additionalAttributes } = this.currentChat;
      const type = additionalAttributes ? additionalAttributes.type : '';
      return type || '';
    },
    maxLength() {
      if (this.isPrivate) {
        return MESSAGE_MAX_LENGTH.GENERAL;
      }
      if (this.isAFacebookInbox) {
        return MESSAGE_MAX_LENGTH.FACEBOOK;
      }
      if (this.isAnInstagramChannel) {
        return MESSAGE_MAX_LENGTH.INSTAGRAM;
      }
      if (this.isATelegramChannel) {
        return MESSAGE_MAX_LENGTH.TELEGRAM;
      }
      if (this.isATiktokChannel) {
        return MESSAGE_MAX_LENGTH.TIKTOK;
      }
      if (this.isATwilioWhatsAppChannel) {
        return MESSAGE_MAX_LENGTH.TWILIO_WHATSAPP;
      }
      if (this.isAWhatsAppCloudChannel) {
        return MESSAGE_MAX_LENGTH.WHATSAPP_CLOUD;
      }
      if (this.isASmsInbox) {
        return MESSAGE_MAX_LENGTH.TWILIO_SMS;
      }
      if (this.isAnEmailChannel) {
        return MESSAGE_MAX_LENGTH.EMAIL;
      }
      if (this.isATwilioSMSChannel) {
        return MESSAGE_MAX_LENGTH.TWILIO_SMS;
      }
      if (this.isAWhatsAppChannel) {
        return MESSAGE_MAX_LENGTH.WHATSAPP_CLOUD;
      }
      return MESSAGE_MAX_LENGTH.GENERAL;
    },
    showFileUpload() {
      return (
        this.isAWebWidgetInbox ||
        this.isAFacebookInbox ||
        this.isAWhatsAppChannel ||
        this.isAPIInbox ||
        this.isAnEmailChannel ||
        this.isASmsInbox ||
        this.isATelegramChannel ||
        this.isALineChannel ||
        this.isAnInstagramChannel ||
        this.isATiktokChannel
      );
    },
    replyButtonLabel() {
      let sendMessageText = this.$t('CONVERSATION.REPLYBOX.SEND');
      if (this.isPrivate) {
        sendMessageText = this.$t('CONVERSATION.REPLYBOX.CREATE');
      }
      const keyLabel = this.isEditorHotKeyEnabled('cmd_enter')
        ? `(${this.shortcutKey})`
        : '(↵)';
      return `${sendMessageText} ${keyLabel}`;
    },
    replyBoxClass() {
      return {
        'is-private': this.isPrivate,
        'is-focused': this.isFocused || this.hasAttachments,
        'reply-box--multi-line':
          this.isReplyBoxMultiline &&
          !this.showAudioRecorderEditor &&
          !this.copilot.isActive.value,
        'reply-box--recording': this.showAudioRecorderEditor,
        'reply-box--with-reply': this.replyCardAttached,
        'reply-box--funil-fixed-height':
          this.isFunilDesktopViewport && !this.isPrivate,
      };
    },
    hasAttachments() {
      return this.attachedFiles.length;
    },
    shouldHideJumpToBottom() {
      return Boolean(
        this.hasAttachments ||
          this.isSendingAttachments ||
          (this.isMobileViewport && this.shouldShowReplyToMessage)
      );
    },
    hasCannedResponseAttachments() {
      return this.attachedFiles.some(
        attachment => attachment.isCannedResponseAttachment
      );
    },
    hasPreviewableAttachments() {
      return this.attachedFiles.some(file => !file?.isRecordedAudio);
    },
    // Aceleres 2026-09-11: a nota interna ficava de fora e caia na previa
    // "compact" — uma pilha branca com o nome do ficheiro por cima do cartao
    // da nota, nada parecida com o que o agente ve ao enviar a mesma imagem.
    // Anexar e anexar: a previa e a mesma nos dois modos, e o que muda e para
    // onde a mensagem vai (`private`), tratado no envio.
    usesMediaGallery() {
      return this.hasAttachments && this.hasPreviewableAttachments;
    },
    // Instagram, Messenger e TikTok nao aceitam texto e anexo na MESMA
    // mensagem. Nos dois canais da Meta que passam pela WAME isto nao e
    // preferencia: `Wame::SendOnSocialChannelService#send_plain_attachment`
    // manda SEMPRE `caption: nil`, e a legenda sai depois, como uma segunda
    // chamada a `/message/text`. Ou seja, o contato ja recebia duas
    // mensagens; so o painel e que desenhava uma bolha so.
    //
    // O Instagram foi separado aqui desde o inicio; o Messenger nao, e por
    // isso divergia — mesma WAME, mesmo caminho de envio, desenho diferente.
    // Separar tambem no Messenger faz a conversa no painel ser igual a que a
    // pessoa ve no telemovel, e mantem cada perna com o seu proprio eco.
    splitsTextAndAttachments() {
      return (
        this.isAnInstagramChannel ||
        this.isATiktokChannel ||
        this.isAFacebookInbox
      );
    },
    isOnWhatsApp() {
      // Channel::Api is included because in this install Api inboxes route
      // to WhatsApp via Evolution. The send/preview flow matches WhatsApp:
      // each attachment is sent as its own message with its own caption.
      return (
        this.isATwilioWhatsAppChannel ||
        this.isAWhatsAppCloudChannel ||
        this.is360DialogWhatsAppChannel ||
        this.isAPIInbox
      );
    },
    showWhatsAppGallery() {
      // Todo anexo público usa a mesma prévia contextual. Áudios gravados
      // continuam no gravador, pois o fluxo deles envia assim que termina.
      // Respostas rápidas também usam a galeria: os anexos pré-enviados não
      // trazem um File local, mas precisam da mesma leitura visual dos envios
      // múltiplos, em vez de aparecerem como chips compactos de 0 Bytes.
      return (
        (this.usesMediaGallery || this.hasCannedResponseAttachments) &&
        !this.suppressWhatsAppGallery
      );
    },
    showAudioRecorder() {
      return !this.isOnPrivateNote && this.showFileUpload;
    },
    showAudioRecorderEditor() {
      return this.showAudioRecorder && this.isRecordingAudio;
    },
    isOnPrivateNote() {
      return this.replyType === REPLY_EDITOR_MODES.NOTE;
    },
    isOnExpandedLayout() {
      const {
        LAYOUT_TYPES: { CONDENSED },
      } = wootConstants;
      const { conversation_display_type: conversationDisplayType = CONDENSED } =
        this.uiSettings;
      return conversationDisplayType !== CONDENSED;
    },
    isMessageEmpty() {
      if (!this.message) {
        return true;
      }
      // O ProseMirror pode manter caracteres invisíveis após apagar o texto.
      // Eles não devem trocar o microfone por um botão de enviar vazio.
      return !this.message.replace(/[\s\u200B-\u200D\uFEFF]/g, '').length;
    },
    showReplyHead() {
      return !this.isOnPrivateNote && this.isAnEmailChannel;
    },
    enableMultipleFileUpload() {
      return (
        this.isAnEmailChannel ||
        this.isAWebWidgetInbox ||
        this.isAPIInbox ||
        this.isAWhatsAppChannel ||
        this.isATelegramChannel
      );
    },
    isSignatureEnabledForInbox() {
      return !this.isPrivate && this.sendWithSignature;
    },
    isSignatureAvailable() {
      return !!this.messageSignature;
    },
    sendWithSignature() {
      return this.fetchSignatureFlagFromUISettings(this.channelType);
    },
    // Aceleres: uma conexão individual própria é pessoal mesmo quando a
    // mensagem ainda não recebeu a etiqueta/atributo de pessoal.
    aceleresIsPersonalConversation() {
      const chat = this.currentChat;
      return isAceleresPersonalConversation({
        conversation: chat,
        inbox: this.inbox,
      });
    },
    // Aceleres 4 caixas: gate do "Iniciar atendimento" — bloqueia o composer e
    // mostra o botão Iniciar. A regra saiu daqui para o helper quando o modal
    // de dados do contato passou a precisar da mesma resposta; o
    // comportamento é o mesmo, só deixou de ter duas versões.
    aceleresNeedsStartGate() {
      return aceleresNeedsStartGate({
        conversation: this.currentChat,
        inbox: this.inbox,
        currentUser: this.currentUser,
      });
    },
    // A primeira resposta pública também pode assumir uma conversa ainda não
    // iniciada que esteja em Outros. O backend atribui a conversa ao remetente
    // depois que a mensagem é criada.
    aceleresCanSendReply() {
      if (
        !this.isAPIInbox ||
        this.isOnPrivateNote ||
        this.isApiInboxConnectionBlocked ||
        this.whatsappInstanceUnavailable
      ) {
        return false;
      }

      const assigneeId = this.currentChat?.meta?.assignee?.id;
      const currentUserId = this.currentUser?.id;
      const isStarted = isAceleresConversationStarted(this.currentChat);
      const isPersonal = this.aceleresIsPersonalConversation;
      const canClaimFirstReply =
        !isStarted &&
        !isPersonal &&
        Boolean(assigneeId) &&
        Boolean(currentUserId) &&
        Number(assigneeId) !== Number(currentUserId);

      return !assigneeId || assigneeId === currentUserId || canClaimFirstReply;
    },
    conversationId() {
      return this.currentChat.id;
    },
    conversationIdByRoute() {
      return this.conversationId;
    },
    editorStateId() {
      return buildDraftKey(this.conversationIdByRoute, this.replyType);
    },
    // Aceleres: assinatura barata da bandeja de anexos. Um watcher em
    // `attachedFiles` sem `deep` não dispara no push (a referência do array
    // não muda) e com `deep` percorreria os data URLs a cada tecla. Esta
    // computed itera a lista, então reage a push/splice/troca do array, e
    // compara só identificadores curtos.
    emojiSheetStyle() {
      if (!this.emojiSheetHeight) return null;
      return { '--acl-emoji-sheet-height': `${this.emojiSheetHeight}px` };
    },
    draftAttachmentsSignature() {
      return this.attachedFiles
        .map(
          attachment =>
            `${attachment?.previewId || attachment?.blobSignedId || attachment?.thumb?.length || ''}:${attachment?.caption || ''}`
        )
        .join('|');
    },
    audioRecordFormat() {
      // A WAME recebe a gravação original e converte para OGG/Opus no servidor.
      // Evita a conversão MP3 no navegador, que falha em alguns dispositivos.
      if (this.isWameAPIInbox) {
        return AUDIO_FORMATS.WEBM;
      }
      if (this.isAWhatsAppChannel || this.isATelegramChannel) {
        return AUDIO_FORMATS.MP3;
      }
      if (this.isAPIInbox) {
        return AUDIO_FORMATS.MP3;
      }
      return AUDIO_FORMATS.WAV;
    },
    messageVariables() {
      const variables = getMessageVariables({
        conversation: this.currentChat,
        contact: this.currentContact,
        inbox: this.inbox,
      });
      return variables;
    },
    connectedPortalSlug() {
      const { help_center: portal = {} } = this.inbox;
      const { slug = '' } = portal;
      return slug;
    },
    isQuotedEmailReplyEnabled() {
      return this.isFeatureEnabledonAccount(
        this.accountId,
        FEATURE_FLAGS.QUOTED_EMAIL_REPLY
      );
    },
    quotedReplyPreference() {
      if (!this.isAnEmailChannel || !this.isQuotedEmailReplyEnabled) {
        return false;
      }

      return !!this.fetchQuotedReplyFlagFromUISettings(this.channelType);
    },
    lastEmailWithQuotedContent() {
      if (!this.isAnEmailChannel) {
        return null;
      }

      const lastEmail = this.lastEmail;
      if (!lastEmail || lastEmail.private) {
        return null;
      }

      return lastEmail;
    },
    quotedEmailText() {
      return extractQuotedEmailText(this.lastEmailWithQuotedContent);
    },
    quotedEmailPreviewText() {
      return truncatePreviewText(this.quotedEmailText, 80);
    },
    shouldShowQuotedReplyToggle() {
      return (
        this.isAnEmailChannel &&
        !this.isOnPrivateNote &&
        this.isQuotedEmailReplyEnabled
      );
    },
    shouldShowQuotedPreview() {
      return (
        this.shouldShowQuotedReplyToggle &&
        this.quotedReplyPreference &&
        !!this.quotedEmailText
      );
    },
    isDefaultEditorMode() {
      return !this.showAudioRecorderEditor && !this.copilot.isActive.value;
    },
    isEditorDisabled() {
      if (!this.currentChat?.id || !this.inboxId) return true;
      if (
        !this.isOnPrivateNote &&
        (this.currentContact?.blocked ?? this.currentChat?.muted)
      ) {
        return true;
      }

      // Conversa concluída: travar a resposta pública assim que o status
      // chegar (ação local do próprio "Concluir" ou broadcast em tempo real
      // de outra aba/usuário) — sem isso o composer seguia digitável e o
      // agente mandava mensagem real pro cliente numa conversa já encerrada.
      // Nota privada continua liberada mesmo com a conversa concluída.
      const {
        STATUS_TYPE: { RESOLVED },
      } = wootConstants;
      if (this.currentChat.status === RESOLVED && !this.isOnPrivateNote) {
        return true;
      }

      // O estado do IndexedDB pode estar atrasado enquanto a inbox é lida
      // diretamente. Até essa leitura confirmar uma conexão WAME, não permitir
      // digitação/envio público; `connecting` também não aceita mensagens.
      if (
        !this.isOnPrivateNote &&
        (this.isApiInboxConnectionBlocked || this.whatsappInstanceUnavailable)
      ) {
        return true;
      }

      // A inbox API do treino pode chegar no primeiro payload com
      // can_reply=false. O backend aceita a resposta normalmente; aguardar um
      // reload aqui impedia justamente a primeira mensagem do vendedor.
      if (this.isTrainingConversationContext) return false;

      // No fluxo Aceleres, a primeira resposta também inicia o atendimento:
      // o callback do Message atribui a conversa ao vendedor e marca
      // attendance_started. O botão continua visível como alternativa, mas
      // não pode bloquear o editor nem o envio por Enter.
      if (this.aceleresCanSendReply) {
        return false;
      }

      // Instagram e Messenger também têm janela de 24h — é política da Meta, e
      // `Conversations::MessageWindowService` já a aplica aos dois canais. Eles
      // faltavam nesta lista, então o compositor NUNCA travava neles: o
      // vendedor digitava, enviava, e só descobria pela bolha vermelha que a
      // Meta recusara (relato de 25/08/2026, conversa 33 — duas mensagens
      // perdidas com a janela fechada havia 28 min).
      //
      // `whatsappInstanceDisconnected` não entra aqui: é estado de conexão
      // WAME de WhatsApp e não descreve estes canais.
      if (this.isAnInstagramChannel || this.isAFacebookInbox) {
        return !this.isOnPrivateNote && this.isOutsideMessagingWindow;
      }

      return (
        (this.isAWhatsAppChannel || this.isAPIInbox) &&
        !this.isOnPrivateNote &&
        (this.isOutsideMessagingWindow || this.whatsappInstanceUnavailable)
      );
    },
    isMetaMessagingWindowExpired() {
      let channelType = null;
      if (this.isAnInstagramChannel) {
        channelType = INBOX_TYPES.INSTAGRAM;
      } else if (this.isAFacebookInbox) {
        channelType = INBOX_TYPES.FB;
      }

      return shouldShowMetaMessagingWindowExpiredNotice({
        channelType,
        isOutsideWindow: this.isOutsideMessagingWindow,
        isPrivateNote: this.isOnPrivateNote,
      });
    },
    // `can_reply` é calculado no servidor UMA vez, quando a conversa carrega, e
    // `addMessage` só o devolve a `true` (mensagem recebida reabre a janela).
    // Nada nunca o devolve a `false`, então uma janela que vence com a aba
    // aberta deixava o compositor destravado — foi exatamente o que aconteceu
    // em 25/08/2026: janela fechada às 17:16, a tela aceitou o envio às 17:44.
    //
    // `messaging_window_expires_at` (epoch, do mesmo MessageWindowService)
    // permite envelhecer sem duplicar regra nenhuma aqui: a tela só compara com
    // o próprio relógio. Sem o campo — payload antigo em cache — cai no
    // comportamento anterior.
    isOutsideMessagingWindow() {
      if (!this.currentChat.can_reply) return true;

      const expiresAt = Number(this.currentChat.messaging_window_expires_at);
      if (!Number.isFinite(expiresAt) || expiresAt <= 0) return false;

      return this.aceleresNowTs >= expiresAt;
    },
    whatsappInstanceDisconnected() {
      const status = this.inbox?.additional_attributes?.connection_status;
      return [
        'disconnected',
        'closed',
        'failed',
        'logout',
        'logged_out',
        'unauthenticated',
      ].includes(String(status || '').toLowerCase());
    },
  },
  watch: {
    shouldHideJumpToBottom: {
      handler(shouldHide) {
        this.$emit('attachmentProcessingChange', shouldHide);
      },
      immediate: true,
    },
    inboxId: {
      handler(inboxId) {
        this.ensureInboxLoaded(inboxId);
      },
      immediate: true,
    },
    'currentChat.inbox_connection_status'(status, previousStatus) {
      if (status !== previousStatus && this.inboxConnectionVerifiedId) {
        this.ensureInboxLoaded(this.inboxId);
      }
    },
    // NOTA (Aceleres 2026-07-03): o envio pós-gravação (botão Enviar da barra
    // WhatsApp) NÃO usa watcher. Em Vue 3 um watcher sobre um array reativo
    // não dispara no .push() (a ref do proxy não muda); e mesmo com deep,
    // oldValue === newValue — logo `files.length > oldFiles.length` seria
    // sempre false. O gatilho vive no attachFile (ponto único onde o arquivo
    // cai na bandeja), controlado pela flag sendOnFinish. Ver sendAudioRecording.
    // Aceleres: este watcher NAO mexe mais no modo do compositor. Ele dispara
    // a cada atualizacao da conversa (websocket, atribuicao, nova mensagem) e
    // o upstream aproveitava a passagem para forcar `NOTE` quando `can_reply`
    // era falso -- era isso que abria a nota interna sozinha no meio do
    // atendimento. Quem decide o modo agora e o agente (setReplyMode) e a
    // troca de conversa (watcher de conversationIdByRoute, que volta para
    // resposta).
    currentChat(conversation, oldConversation) {
      if (oldConversation && oldConversation.id !== conversation.id) {
        // Only update email fields when switching to a completely different conversation (by ID)
        // This prevents overwriting user input (e.g., CC/BCC fields) when performing actions
        // like self-assign or other updates that do not actually change the conversation context
        this.setCCAndToEmailsFromLastChat();
        // Reset Copilot editor state (includes cancelling ongoing generation)
        this.copilot.reset();
      }

      this.fetchAndSetReplyTo();
    },
    // When moving from one conversation to another, the store may not have the
    // list of all the messages. A fetch is subsequently made to get the messages.
    // This watcher handles two main cases:
    // 1. When switching conversations and messages are fetched/updated, ensures CC/BCC fields are set from the latest OUTGOING/INCOMING email (not activity/private messages).
    // 2. Fixes and issue where CC/BCC fields could be reset/lost after assignment/activity actions or message mutations that did not represent a true email context change.
    lastEmail: {
      handler(lastEmail) {
        if (!lastEmail) return;
        this.setCCAndToEmailsFromLastChat();
      },
      deep: true,
    },
    conversationIdByRoute(conversationId, oldConversationId) {
      if (conversationId !== oldConversationId) {
        this.inboxConnectionVerifiedId = null;
        this.inboxConnectionVerifiedConversationId = null;
        this.verifiedInbox = null;
        this.ensureInboxLoaded(this.inboxId);
        this.setToDraft(oldConversationId, this.replyType);
        // A limpeza passou para antes do `getFromDraft`: agora ele restaura os
        // anexos da conversa nova e, na ordem antiga, o reset apagaria o que
        // acabou de ser restaurado. Os anexos da conversa antiga já estão na
        // store (o watcher de `draftAttachmentsSignature` grava a cada
        // mudança), então esvaziar a bandeja aqui não perde nada.
        this.resetRecorderAndClearAttachments();
        // Aceleres: conversa nova comeca sempre em resposta. Sem isso a nota
        // interna que o agente abriu na conversa anterior o seguiria pela
        // lista, e a proxima conversa apareceria com o bloco de notas aberto
        // sem ele ter pedido. A troca acontece aqui (e nao no watcher de
        // currentChat) porque so aqui o rascunho antigo ja foi salvo sob o id
        // certo.
        this.applyReplyModeWithoutDraftSync(REPLY_EDITOR_MODES.REPLY);
        this.getFromDraft();
      }
    },
    // Espelha a bandeja na store a cada anexo adicionado, removido, legendado
    // ou limpo — inclusive na limpeza pós-envio, que é o que apaga o rascunho.
    draftAttachmentsSignature() {
      this.persistDraftAttachments();
    },
    message() {
      // Autosave the current message draft.
      this.doAutoSaveDraft();
      this.$nextTick(() => this.syncReplyBoxMultilineState());
    },
    replyType(updatedReplyType, oldReplyType) {
      // A troca feita durante a mudanca de conversa ja cuidou dos rascunhos
      // (com os ids certos); repetir aqui gravaria o rascunho da conversa
      // antiga sob o id da nova.
      if (this.skipReplyTypeDraftSync) {
        this.skipReplyTypeDraftSync = false;
        return;
      }
      this.setToDraft(this.conversationIdByRoute, oldReplyType);
      this.getFromDraft();
    },
  },

  mounted() {
    // O seletor fica pronto em segundo plano antes do primeiro clique na carinha.
    const preloadEmojiInput = () => loadEmojiInput().catch(() => {});
    if (window.requestIdleCallback) {
      window.requestIdleCallback(preloadEmojiInput, { timeout: 250 });
    } else {
      window.setTimeout(preloadEmojiInput, 0);
    }
    this.aceleresWindowTimer = setInterval(() => {
      this.aceleresNowTs = Math.floor(Date.now() / 1000);
    }, 60000);
    this.getFromDraft();
    // O WootMessageEditor emite o envio por teclado diretamente para este
    // componente, inclusive quando o editor está focado.
    document.addEventListener('paste', this.onPaste);
    this.replyBoxKeydownCapture = this.onReplyBoxKeydownCapture.bind(this);
    document.addEventListener('keydown', this.replyBoxKeydownCapture, true);
    document.addEventListener('dragenter', this.handleConversationDragEnter);
    document.addEventListener('dragover', this.handleConversationDragOver);
    document.addEventListener('dragleave', this.handleConversationDragLeave);
    document.addEventListener('drop', this.handleConversationDrop);
    this.setCCAndToEmailsFromLastChat();
    this.doAutoSaveDraft = debounce(
      () => {
        this.saveDraft(this.conversationIdByRoute, this.replyType);
      },
      500,
      true
    );

    this.fetchAndSetReplyTo();
    emitter.on(BUS_EVENTS.TOGGLE_REPLY_TO_MESSAGE, this.onReplyToMessage);
    // O botão "Iniciar atendimento" do modal de dados do contato dispara este
    // evento em vez de repetir a ação: ela mexe em rodízio, atribuição e
    // store, e uma segunda cópia sairia de sincronia na primeira mudança.
    emitter.on(
      BUS_EVENTS.ACELERES_START_CONVERSATION_REQUESTED,
      this.onAceleresStartConversationRequested
    );

    // A hacky fix to solve the drag and drop
    // Is showing on top of new conversation modal drag and drop
    // TODO need to find a better solution
    emitter.on(
      BUS_EVENTS.NEW_CONVERSATION_MODAL,
      this.onNewConversationModalActive
    );
    emitter.on(BUS_EVENTS.INSERT_INTO_NORMAL_EDITOR, this.addIntoEditor);
    emitter.on(CMD_AI_ASSIST, this.executeCopilotAction);
    emitter.on(BUS_EVENTS.ADD_MESSAGE_TO_NOTE, this.onAddMessageToNote);
    emitter.on(BUS_EVENTS.EMOJI_PICKER_OPENED, this.onEmojiPickerOpened);
    emitter.on(
      BUS_EVENTS.OPEN_REACTION_EMOJI_SHEET,
      this.onOpenReactionEmojiSheet
    );
    this.$nextTick(() => this.setupReplyBoxResizeObserver());
    window.addEventListener('resize', this.refreshEmojiPickerPosition);
    window.addEventListener('scroll', this.refreshEmojiPickerPosition, true);
    window.visualViewport?.addEventListener(
      'resize',
      this.refreshEmojiPickerPosition
    );
    window.visualViewport?.addEventListener(
      'scroll',
      this.refreshEmojiPickerPosition
    );
    window.addEventListener('beforeunload', this.warnBeforeLosingUnsentMedia);
  },
  unmounted() {
    window.removeEventListener(
      'beforeunload',
      this.warnBeforeLosingUnsentMedia
    );
    if (this.aceleresWindowTimer) {
      clearInterval(this.aceleresWindowTimer);
      this.aceleresWindowTimer = null;
    }
    document.removeEventListener('paste', this.onPaste);
    if (this.replyBoxKeydownCapture) {
      document.removeEventListener(
        'keydown',
        this.replyBoxKeydownCapture,
        true
      );
      this.replyBoxKeydownCapture = null;
    }
    document.removeEventListener('dragenter', this.handleConversationDragEnter);
    document.removeEventListener('dragover', this.handleConversationDragOver);
    document.removeEventListener('dragleave', this.handleConversationDragLeave);
    document.removeEventListener('drop', this.handleConversationDrop);
    emitter.off(BUS_EVENTS.TOGGLE_REPLY_TO_MESSAGE, this.onReplyToMessage);
    emitter.off(
      BUS_EVENTS.ACELERES_START_CONVERSATION_REQUESTED,
      this.onAceleresStartConversationRequested
    );
    emitter.off(BUS_EVENTS.INSERT_INTO_NORMAL_EDITOR, this.addIntoEditor);
    emitter.off(BUS_EVENTS.ADD_MESSAGE_TO_NOTE, this.onAddMessageToNote);
    emitter.off(BUS_EVENTS.EMOJI_PICKER_OPENED, this.onEmojiPickerOpened);
    emitter.off(
      BUS_EVENTS.OPEN_REACTION_EMOJI_SHEET,
      this.onOpenReactionEmojiSheet
    );
    emitter.off(
      BUS_EVENTS.NEW_CONVERSATION_MODAL,
      this.onNewConversationModalActive
    );
    emitter.off(CMD_AI_ASSIST, this.executeCopilotAction);
    this.disconnectReplyBoxResizeObserver();
    window.removeEventListener('resize', this.refreshEmojiPickerPosition);
    window.removeEventListener('scroll', this.refreshEmojiPickerPosition, true);
    window.visualViewport?.removeEventListener(
      'resize',
      this.refreshEmojiPickerPosition
    );
    window.visualViewport?.removeEventListener(
      'scroll',
      this.refreshEmojiPickerPosition
    );
  },
  methods: {
    isFileDragEvent(event) {
      const dataTransfer = event?.dataTransfer;
      if (!dataTransfer) return false;

      if (dataTransfer.files?.length) return true;

      return Array.from(dataTransfer.types || []).includes('Files');
    },
    isConversationDropTarget(target) {
      const element = target?.nodeType === 1 ? target : target?.parentElement;

      return Boolean(
        element?.closest?.('.conversation-details-wrap.chat-whats')
      );
    },
    canDropConversationFile() {
      return (
        !this.newConversationModalActive &&
        !this.isEditorDisabled &&
        (this.showFileUpload || this.isPrivate)
      );
    },
    handleConversationDragEnter(event) {
      if (
        !this.canDropConversationFile() ||
        !this.isFileDragEvent(event) ||
        !this.isConversationDropTarget(event.target)
      ) {
        return;
      }

      this.isConversationFileDragActive = true;
    },
    handleConversationDragOver(event) {
      if (
        !this.canDropConversationFile() ||
        !this.isFileDragEvent(event) ||
        !this.isConversationDropTarget(event.target)
      ) {
        return;
      }

      event.preventDefault();
      this.isConversationFileDragActive = true;
    },
    handleConversationDragLeave(event) {
      if (!this.isConversationFileDragActive || !this.isFileDragEvent(event)) {
        return;
      }

      if (
        event.relatedTarget &&
        this.isConversationDropTarget(event.relatedTarget)
      ) {
        return;
      }

      this.isConversationFileDragActive = false;
    },
    handleConversationDrop(event) {
      if (this.isFileDragEvent(event)) {
        this.isConversationFileDragActive = false;
      }
    },
    async ensureInboxLoaded(inboxId) {
      const id = Number(inboxId);
      if (!Number.isFinite(id) || id <= 0) return;
      const conversationId = Number(this.currentChat?.id || 0);

      this.inboxConnectionVerifiedId = null;
      this.inboxConnectionVerifiedConversationId = null;
      this.verifiedInbox = null;
      this.inboxConnectionVerificationFailedId = null;
      this.inboxConnectionVerificationFailedConversationId = null;
      const inbox = await this.$store.dispatch('inboxes/fetch', id);

      // Uma resposta atrasada de uma conversa anterior não valida a inbox
      // atualmente aberta. Falha de rede também não pode liberar o composer
      // com o estado antigo do IndexedDB.
      if (
        id !== Number(this.inboxId) ||
        conversationId !== Number(this.currentChat?.id || 0)
      )
        return;
      if (!inbox) {
        this.inboxConnectionVerificationFailedId = id;
        this.inboxConnectionVerificationFailedConversationId = conversationId;
        return;
      }

      this.verifiedInbox = inbox;
      this.inboxConnectionVerifiedId = id;
      this.inboxConnectionVerifiedConversationId = conversationId;
      this.inboxConnectionVerificationFailedId = null;
      this.inboxConnectionVerificationFailedConversationId = null;
    },
    syncReplyBoxMultilineState() {
      const editorElement = this.replyEditor;
      if (!editorElement) return;

      // Aceleres 2026-09-09: com o Jarvis aberto, o unico `.ProseMirror`
      // dentro da `.reply-box` e o DELE -- o compositor nem chega a montar o
      // seu (`v-else-if` no CopilotEditorSection). A medida abaixo passava a
      // ser a do campo da resposta sugerida e, ao escrever la, deixava
      // `isReplyBoxMultiline` a `true`.
      //
      // Nao se via enquanto o painel estava aberto, porque `replyBoxClass`
      // segura a classe com `!copilot.isActive`. Via-se ao FECHAR: a guarda
      // caia, a classe entrava com o estado velho, e a barra branca voltava na
      // forma de duas linhas (52px fixos, cantos a 28px) com o campo vazio.
      //
      // So aparecia agora porque o campo do Jarvis passou a crescer com o
      // texto; enquanto esteve preso a uma linha, nunca chegava ao limiar.
      //
      // Zera-se em vez de so sair: e o que garante que a barra volta na forma
      // de uma linha no instante em que o painel fecha.
      if (this.copilot.isActive.value) {
        this.isReplyBoxMultiline = false;
        return;
      }

      const proseMirror = editorElement.querySelector('.ProseMirror');
      if (!proseMirror) {
        this.isReplyBoxMultiline = false;
        return;
      }

      const styles = window.getComputedStyle(proseMirror);
      const lineHeight = Number.parseFloat(styles.lineHeight) || 20;
      const verticalPadding =
        (Number.parseFloat(styles.paddingTop) || 0) +
        (Number.parseFloat(styles.paddingBottom) || 0);
      const contentHeight = Math.max(
        0,
        proseMirror.scrollHeight - verticalPadding
      );

      // Aceleres: evita o "tremor" dos ícones laterais enquanto o ProseMirror
      // recalcula altura. Sem histerese, o scrollHeight oscila no limite entre
      // 1 e 2 linhas e a classe reply-box--multi-line liga/desliga no mesmo
      // trecho de digitação.
      const enterMultilineAt = lineHeight * 1.65;
      const leaveMultilineAt = lineHeight * 1.2;
      const nextIsMultiline = this.isReplyBoxMultiline
        ? contentHeight > leaveMultilineAt
        : contentHeight > enterMultilineAt;

      if (nextIsMultiline !== this.isReplyBoxMultiline) {
        this.isReplyBoxMultiline = nextIsMultiline;
      }
    },
    syncReplyBoxDraftHeight() {
      const editorElement = this.replyEditor;
      const proseMirror = editorElement?.querySelector('.ProseMirror');
      if (!proseMirror) return;

      if (this.isFunilDesktopViewport && !this.isPrivate) {
        this.clearReplyBoxDraftHeight();
        return;
      }

      if (
        !this.isFocused ||
        this.copilot.isActive.value ||
        !proseMirror.textContent?.trim()
      ) {
        this.clearReplyBoxDraftHeight();
        return;
      }

      // Mantem a altura medida durante a digitacao. Recalcular apos cada
      // alteracao permite crescer ou encolher com o texto, sem encolher durante
      // uma pausa enquanto o mesmo rascunho continua focado.
      proseMirror.style.removeProperty('min-height');
      const editorHeight = Math.ceil(proseMirror.getBoundingClientRect().height);
      if (editorHeight > 0) {
        proseMirror.style.setProperty(
          'min-height',
          `${editorHeight}px`,
          'important'
        );
      }
    },
    clearReplyBoxDraftHeight() {
      const proseMirror = this.replyEditor?.querySelector('.ProseMirror');
      proseMirror?.style.removeProperty('min-height');
    },
    setupReplyBoxResizeObserver() {
      this.disconnectReplyBoxResizeObserver();
      const editorElement = this.replyEditor;
      if (!editorElement) return;

      editorElement.addEventListener('input', this.syncReplyBoxDraftHeight);

      if (typeof window === 'undefined' || !window.ResizeObserver) {
        this.syncReplyBoxMultilineState();
        return;
      }

      this.replyBoxResizeObserver = new window.ResizeObserver(() => {
        if (this.replyBoxResizeFrame) {
          window.cancelAnimationFrame(this.replyBoxResizeFrame);
        }
        this.replyBoxResizeFrame = window.requestAnimationFrame(() => {
          this.replyBoxResizeFrame = null;
          this.syncReplyBoxMultilineState();
        });
      });
      this.replyBoxResizeObserver.observe(editorElement);
      this.syncReplyBoxMultilineState();
    },
    disconnectReplyBoxResizeObserver() {
      this.replyEditor?.removeEventListener(
        'input',
        this.syncReplyBoxDraftHeight
      );
      this.clearReplyBoxDraftHeight();
      if (this.replyBoxResizeFrame) {
        window.cancelAnimationFrame(this.replyBoxResizeFrame);
        this.replyBoxResizeFrame = null;
      }
      this.replyBoxResizeObserver?.disconnect();
      this.replyBoxResizeObserver = null;
    },
    onAceleresStartConversationRequested({ conversationId } = {}) {
      if (conversationId && conversationId !== this.currentChat?.id) return;
      this.aceleresStartConversation();
    },
    // Aceleres: botão "Iniciar atendimento" registra o início, trava o rodízio,
    // abre o composer e move de NOVOS para MEUS.
    async aceleresStartConversation({ silent = false } = {}) {
      if (this.aceleresStarting) return false;
      const chat = this.currentChat;
      const conversationDisplayId =
        chat?.display_id ||
        this.$route?.params?.conversation_id ||
        this.$route?.params?.conversationId ||
        this.$route?.params?.id;
      if (!chat || !conversationDisplayId) return false;
      this.aceleresStarting = true;
      try {
        const response = await AceleresTeamPresenceAPI.lockConversation({
          conversationDisplayId,
        });
        const c = this.currentChat || {};
        const currentUpdatedAt = Number(c.updated_at) || 0;
        const updatedAt = Math.max(
          currentUpdatedAt + 1,
          Math.floor(Date.now() / 1000)
        );
        const currentUser = this.currentUser || {};
        const assignee = response?.data?.assignee || {
          id: currentUser.id,
          name: currentUser.name,
          available_name: currentUser.available_name,
          email: currentUser.email,
          thumbnail: currentUser.thumbnail,
        };
        const startedAttributes = {
          attendance_started: true,
          rodizio_locked: true,
          rodizio_locked_by: currentUser.id,
          rodizio_locked_reason: 'iniciar_button',
          rodizio_locked_at: new Date().toISOString(),
        };
        // Uma listagem pedida ANTES deste clique ainda pode chegar depois dele,
        // descrevendo a conversa sem responsável e sem `attendance_started`. Sem
        // este registro o gate volta a desenhar o botão e o atendimento parece
        // não ter começado. Ver pendingStartWrites na store de conversas.
        this.$store.dispatch('markConversationAttendanceStarted', {
          conversationId: c.id,
          assignee,
          attributes: startedAttributes,
        });
        this.$store.dispatch('updateConversation', {
          ...c,
          updated_at: updatedAt,
          meta: {
            ...(c.meta || {}),
            assignee,
          },
          additional_attributes: {
            ...(c.additional_attributes || {}),
            ...startedAttributes,
          },
        });
        await this.$store.dispatch('getConversation', c.id);
        emitter.emit(BUS_EVENTS.ACELERES_CONVERSATION_STARTED, {
          conversationId: c.id,
          displayId: conversationDisplayId,
        });
        if (!silent) useAlert('Atendimento iniciado e movido para Meus.');
        return true;
      } catch (e) {
        // `silent` cala apenas o aviso de sucesso (o envio que dispara o gate
        // sozinho nao precisa de toast). A falha SEMPRE aparece: sem isto o
        // audio gravado era descartado sem que o vendedor visse o motivo.
        useAlert(
          e?.response?.data?.error ||
            'Não foi possível iniciar o atendimento. Tente novamente.',
          { duration: SEND_ERROR_ALERT_DURATION }
        );
        return false;
      } finally {
        this.aceleresStarting = false;
      }
    },
    // Aceleres: abre nota privada pre-preenchida com o texto da mensagem
    onAddMessageToNote({ content, conversationId }) {
      if (this.conversationIdByRoute !== conversationId) return;
      this.replyType = REPLY_EDITOR_MODES.NOTE;
      this.$nextTick(() => {
        if (content) this.message = content;
      });
    },
    getDraftKey(
      conversationId = this.conversationIdByRoute,
      replyType = this.replyType
    ) {
      return buildDraftKey(conversationId, replyType);
    },
    getCopilotAcceptedMessage(replyType = this.replyType) {
      const key = this.getDraftKey(this.conversationIdByRoute, replyType);
      return this.copilotAcceptedMessages[key] || '';
    },
    setCopilotAcceptedMessage(message, replyType = this.replyType) {
      const key = this.getDraftKey(this.conversationIdByRoute, replyType);
      this.copilotAcceptedMessages[key] = trimContent(
        message || '',
        this.maxLength
      );
    },
    clearCopilotAcceptedMessage(replyType = this.replyType) {
      const key = this.getDraftKey(this.conversationIdByRoute, replyType);
      delete this.copilotAcceptedMessages[key];
    },
    handleInsert(article) {
      const { url, title } = article;
      // Removing empty lines from the title
      const lines = title.split('\n');
      const nonEmptyLines = lines.filter(line => line.trim() !== '');
      const filteredMarkdown = nonEmptyLines.join(' ');
      emitter.emit(
        BUS_EVENTS.INSERT_INTO_RICH_EDITOR,
        `[${filteredMarkdown}](${url})`
      );

      useTrack(CONVERSATION_EVENTS.INSERT_ARTICLE_LINK);
    },
    toggleQuotedReply() {
      if (!this.isAnEmailChannel) {
        return;
      }

      const nextValue = !this.quotedReplyPreference;
      this.setQuotedReplyFlagForInbox(this.channelType, nextValue);
    },
    shouldIncludeQuotedEmail() {
      return (
        this.isQuotedEmailReplyEnabled &&
        this.quotedReplyPreference &&
        this.shouldShowQuotedReplyToggle &&
        !!this.quotedEmailText
      );
    },
    getMessageWithQuotedEmailText(message) {
      if (!this.shouldIncludeQuotedEmail()) {
        return message;
      }

      const quotedText = this.quotedEmailText || '';
      const header = buildQuotedEmailHeader(
        this.lastEmailWithQuotedContent,
        this.currentContact,
        this.inbox
      );

      return appendQuotedTextToMessage(message, quotedText, header);
    },
    resetRecorderAndClearAttachments() {
      // Reset audio recorder UI state
      this.resetAudioRecorderInput();
      // Reset attached files
      this.attachedFiles = [];
      // Aceleres: o picker de emoji suprime a galeria enquanto está aberto. Se
      // a conversa muda com ele aberto, a bandeja restaurada da conversa nova
      // não apareceria na galeria; fechar aqui devolve o compositor ao estado
      // neutro antes da restauração.
      this.hideEmojiPicker();
    },
    saveDraft(conversationId, replyType) {
      if (this.message || this.message === '') {
        const key = this.getDraftKey(conversationId, replyType);
        const draftToSave = trimContent(this.message || '', this.maxLength);

        this.$store.dispatch('draftMessages/set', {
          key,
          message: draftToSave,
          preview: this.buildDraftPreview(draftToSave),
        });
      }
    },
    // Aceleres: texto que a lista de conversas mostra depois de "Rascunho:".
    // Precisa sair daqui porque só o compositor sabe se a assinatura foi
    // anexada ao rascunho — sem tirá-la, toda conversa que o agente abriu
    // apareceria com rascunho, já que a assinatura entra no editor sozinha.
    buildDraftPreview(rawMessage) {
      const message = rawMessage || '';
      if (!message.trim()) return '';
      if (this.isPrivate) return message.trim();

      const effectiveChannelType = getEffectiveChannelType(
        this.channelType,
        this.inbox?.medium || ''
      );

      return removeSignature(
        message,
        this.messageSignature,
        effectiveChannelType
      ).trim();
    },
    // Aceleres: a bandeja de anexos não sobrevivia à troca de conversa —
    // `resetRecorderAndClearAttachments` a esvaziava e o arquivo se perdia.
    // Agora ela é espelhada na store por conversa (e não por tipo de resposta,
    // para o alternar entre resposta e nota continuar valendo como hoje).
    persistDraftAttachments(conversationId = this.conversationIdByRoute) {
      if (!conversationId) return;
      this.$store.dispatch('draftMessages/setAttachments', {
        conversationId,
        attachments: [...this.attachedFiles],
      });
    },
    restoreDraftAttachments() {
      if (!this.conversationIdByRoute) return;
      const stored = this.$store.getters['draftMessages/getAttachments'](
        this.conversationIdByRoute
      );
      this.attachedFiles = stored.length ? [...stored] : [];
    },
    setToDraft(conversationId, replyType) {
      this.saveDraft(conversationId, replyType);
      this.message = '';
    },
    getFromDraft() {
      if (this.conversationIdByRoute) {
        const key = this.getDraftKey();
        const messageFromStore =
          this.$store.getters['draftMessages/get'](key) || '';

        // ensure that the message has signature set based on the ui setting
        this.message = this.toggleSignatureForDraft(messageFromStore);
        this.restoreDraftAttachments();
      }
    },
    toggleSignatureForDraft(message) {
      if (this.isPrivate) {
        return message;
      }

      // Even when editor is disabled (e.g. WhatsApp/API can't reply), we must
      // still normalize stale signatures out of drafts when signature is off.
      if (this.isEditorDisabled && this.sendWithSignature) {
        return message;
      }

      const effectiveChannelType = getEffectiveChannelType(
        this.channelType,
        this.inbox?.medium || ''
      );

      return this.sendWithSignature
        ? appendSignature(message, this.messageSignature, effectiveChannelType)
        : removeSignature(message, this.messageSignature, effectiveChannelType);
    },
    removeFromDraft() {
      if (this.conversationIdByRoute) {
        const key = this.getDraftKey();
        this.$store.dispatch('draftMessages/delete', { key });
      }
    },
    getComposerSnapshot() {
      return {
        message: this.message,
        attachedFiles: [...this.attachedFiles],
        inReplyTo: this.inReplyTo,
        ccEmails: this.ccEmails,
        bccEmails: this.bccEmails,
        toEmails: this.toEmails,
      };
    },
    clearComposerAfterSend() {
      /* Aceleres 2026-09-11 -- no telemovel, enviar levantava o teclado.

         O `focus-on-mount` do editor governa DUAS rotas de foco, e este
         metodo passa pelas duas:

         1. `forceSetContent()` -> `reloadState()` -> `focusEditor()`, que
            chama `focusEditorInputField('end')` (Editor.vue:444). Acontece em
            QUALQUER envio, mesmo o de texto puro.
         2. o `onMounted` do editor (Editor.vue:963), que corre quando o
            `clearMessage()` abaixo esvazia `attachedFiles`/`isRecordingAudio`,
            a galeria ou o gravador desaparecem e o editor volta a montar --
            e o caso da foto, do ficheiro e do audio.

         A flag sozinha nao chega para a rota 1: `forceSetContent` e chamado de
         forma sincrona aqui, e as props do filho so mudam no render seguinte,
         portanto o editor ainda leria o valor antigo. Dai o `blur()` logo a
         seguir, no mesmo passo -- e o mesmo par (flag + blur) que
         `removeAttachment` e `closeCopilotEditor` ja usam neste ficheiro. A
         flag cobre a rota 2, cuja montagem acontece ja com a prop nova.

         So no telemovel. No computador manter o foco e util: escreve-se a
         mensagem seguinte sem tirar a mao do teclado. */
      const suprimirFoco = this.isMobileViewport;
      if (suprimirFoco) this.suppressEditorFocusOnMount = true;

      this.clearMessage();
      this.removeFromDraft();
      this.$refs.messageEditor?.forceSetContent?.(this.message || '');
      this.isReplyBoxMultiline = false;

      if (suprimirFoco) {
        document.activeElement?.blur?.();
        this.$nextTick(() => {
          document.activeElement?.blur?.();
          this.suppressEditorFocusOnMount = false;
        });
      }
    },
    restoreComposerSnapshot(snapshot, { preserveAttachedFiles = false } = {}) {
      if (!snapshot) return;
      this.message = snapshot.message;
      if (!preserveAttachedFiles) {
        this.attachedFiles = snapshot.attachedFiles;
      }
      this.inReplyTo = snapshot.inReplyTo;
      this.ccEmails = snapshot.ccEmails;
      this.bccEmails = snapshot.bccEmails;
      this.toEmails = snapshot.toEmails;
      this.$nextTick(() => {
        this.$refs.messageEditor?.forceSetContent?.(this.message || '');
        this.syncReplyBoxMultilineState();
        this.saveDraft(this.conversationIdByRoute, this.replyType);
      });
    },
    getElementToBind() {
      return this.replyEditor;
    },
    getKeyboardEvents() {
      return {
        Escape: {
          action: () => {
            this.hideEmojiPicker();
          },
          allowOnFocusedInput: true,
        },
        '$mod+KeyK': {
          action: e => {
            e.preventDefault();
            if (this.isCurrentAccountSeller) return;

            const ninja = document.querySelector('ninja-keys');
            ninja.open();
          },
          allowOnFocusedInput: true,
        },
        Enter: {
          action: e => {
            const isReplyEditorTarget = this.replyEditor?.contains(e.target);
            const canSendFromReplyEditor =
              isReplyEditorTarget &&
              !this.isEditorDisabled &&
              !this.showUserMentions &&
              !this.showMentions &&
              !this.showCannedMenu &&
              !this.showVariablesMenu &&
              this.isEditorHotKeyEnabled('enter');

            if (canSendFromReplyEditor || this.isAValidEvent('enter')) {
              this.onSendReply();
              e.preventDefault();
            }
          },
          allowOnFocusedInput: true,
        },
        '$mod+Enter': {
          action: () => {
            if (this.copilot.isActive.value && this.isFocused) {
              this.onSubmitCopilotReply();
            } else if (this.isAValidEvent('cmd_enter')) {
              this.onSendReply();
            }
          },
          allowOnFocusedInput: true,
        },
      };
    },
    isAValidEvent(selectedKey) {
      return (
        !this.showUserMentions &&
        !this.showMentions &&
        !this.showCannedMenu &&
        !this.showVariablesMenu &&
        this.isFocused &&
        this.isEditorHotKeyEnabled(selectedKey)
      );
    },
    onPaste(e) {
      // Don't handle paste if compose new conversation modal is open
      if (this.newConversationModalActive) return;

      // Don't handle paste if editor is disabled
      if (this.isEditorDisabled) return;

      // Filter valid files (non-zero size)
      Array.from(e.clipboardData.files)
        .filter(file => file.size > 0)
        .filter(file => {
          const isAllowed = isFileTypeAllowedForChannel(file, {
            channelType: this.channelType || this.inbox?.channel_type,
            medium: this.inbox?.medium,
            conversationType: this.conversationType,
            isInstagramChannel: this.isAnInstagramChannel,
            isOnPrivateNote: this.isOnPrivateNote,
          });

          if (!isAllowed) {
            useAlert(
              this.$t('CONVERSATION.FILE_TYPE_NOT_SUPPORTED', {
                fileName: file.name,
              })
            );
          }

          return isAllowed;
        })
        .forEach(file => {
          const { name, type, size } = file;
          this.prepareMediaUpload({ name, type, size, file });
        });
    },
    toggleUserMention(currentMentionState) {
      this.showUserMentions = currentMentionState;
    },
    toggleCannedMenu(value) {
      this.showCannedMenu = value;
    },
    toggleAceleresCannedPopover() {
      // Aceleres: botão "Respostas rápidas" no ReplyBottomPanel abre o
      // popover custom (AceleresCannedPopover) que lista as respostas
      // rápidas e permite criar uma nova caso não existam.
      const shouldOpen = !this.showAceleresCannedPopover;
      if (
        shouldOpen &&
        window.matchMedia('(max-width: 767px), (pointer: coarse)').matches
      ) {
        document.activeElement?.blur?.();
      }
      this.showAceleresCannedPopover = shouldOpen;
    },
    closeAceleresCannedPopover() {
      this.showAceleresCannedPopover = false;
    },
    onAceleresCannedInsert(payload) {
      if (!payload || typeof payload.content !== 'string') return;
      // Insere o conteúdo no editor; o popover já fecha sozinho ao emitir.
      emitter.emit(BUS_EVENTS.INSERT_INTO_RICH_EDITOR, payload.content);
      const attachments = Array.isArray(payload.attachments)
        ? payload.attachments
        : [];
      if (attachments.length) {
        attachments.forEach((attachment, index) => {
          this.handleCannedAttachment({
            attachmentUrl:
              attachment.attachment_url || attachment.attachmentUrl || null,
            attachmentSignedId:
              attachment.attachment_signed_id ||
              attachment.attachmentSignedId ||
              null,
            fileName: attachment.file_name || attachment.fileName || '',
            fileType: attachment.file_type || attachment.fileType || '',
            fileSize: attachment.file_size || attachment.fileSize || 0,
            caption: attachment.caption || (index === 0 ? payload.content : ''),
          });
        });
      } else if (payload.attachmentSignedId) {
        this.handleCannedAttachment({
          attachmentUrl: payload.attachmentUrl,
          attachmentSignedId: payload.attachmentSignedId,
          fileName: payload.fileName || '',
          fileType: payload.fileType || '',
          fileSize: payload.fileSize || 0,
          caption: payload.content,
        });
      }
    },
    toggleVariablesMenu(value) {
      this.showVariablesMenu = value;
    },
    openWhatsappTemplateModal() {
      this.showWhatsAppTemplatesModal = true;
    },
    hideWhatsappTemplatesModal() {
      this.showWhatsAppTemplatesModal = false;
    },
    openContentTemplateModal() {
      this.showContentTemplatesModal = true;
    },
    hideContentTemplatesModal() {
      this.showContentTemplatesModal = false;
    },
    async confirmOnSendReply() {
      // Enter executa o mesmo início/atribuição do botão antes de enviar.
      if (this.aceleresNeedsStartGate) {
        const started = await this.aceleresStartConversation({ silent: true });
        if (!started) return false;
      }

      if (this.isReplyButtonDisabled) {
        return false;
      }
      if (
        !this.isPrivate &&
        this.attachedFiles.length > 0 &&
        this.attachedFiles.every(attachment => this.isGifAttachment(attachment))
      ) {
        const sent = await this.sendAttachedGifsDirectly();
        if (!sent) return false;
        this.clearComposerAfterSend();
        this.hideEmojiPicker();
        return true;
      }
      if (!this.showMentions) {
        const copilotAcceptedMessage = this.getCopilotAcceptedMessage();
        const composerSnapshot = this.getComposerSnapshot();
        const editorMessage = this.message;
        // Local alias of the computed `isOnWhatsApp` so this branch follows
        // the gallery flow (one outgoing message per attachment with its own
        // caption). Includes Channel::Api (WhatsApp via Evolution in this install).
        const isOnWhatsApp = this.isOnWhatsApp;
        // Instagram, Messenger e TikTok nao suportam texto e anexo na mesma
        // mensagem: nos canais da Meta cada componente gera o seu proprio eco,
        // e na TikTok a API recusa a mistura. Ver `splitsTextAndAttachments`.
        const splitsTextAndAttachments = this.splitsTextAndAttachments;
        // A galeria da uma legenda a cada anexo, entao cada anexo vira a sua
        // propria mensagem — inclusive na nota interna, senao as legendas que
        // o agente acabou de escrever seriam descartadas no envio. Os outros
        // motivos de divisao (WhatsApp, Meta, respostas rapidas) sao do canal
        // externo e continuam sem efeito numa nota.
        const sendsMultipleMessages =
          this.usesMediaGallery ||
          ((isOnWhatsApp ||
            splitsTextAndAttachments ||
            this.hasCannedResponseAttachments) &&
            !this.isPrivate);
        let sentSuccessfully;
        if (sendsMultipleMessages) {
          const messages = this.getMultipleMessagesPayload(editorMessage);
          if (!messages.length) return false;
          this.clearComposerAfterSend();
          sentSuccessfully = await this.sendMessageAsMultipleMessages(
            editorMessage,
            copilotAcceptedMessage,
            messages,
            composerSnapshot.attachedFiles
          );
        } else {
          const messagePayload = this.getMessagePayload(editorMessage);
          this.clearComposerAfterSend();
          sentSuccessfully = await this.sendMessage(
            messagePayload,
            editorMessage,
            copilotAcceptedMessage
          );
        }

        if (!sentSuccessfully) {
          this.restoreComposerSnapshot(composerSnapshot, {
            preserveAttachedFiles: sendsMultipleMessages,
          });
          return false;
        }

        if (!this.isPrivate) {
          this.clearEmailField();
        }

        this.hideEmojiPicker();
        return true;
      }
      return false;
    },
    onEditorSend(event) {
      // O ProseMirror só emite este evento depois de reconhecer um Enter
      // válido. Não depender de isFocused aqui evita perder a primeira
      // mensagem quando o foco ainda não foi refletido no estado do Vue.
      if (
        this.showUserMentions ||
        this.showMentions ||
        this.showCannedMenu ||
        this.showVariablesMenu
      ) {
        return;
      }

      // O editor já recebeu a tecla; evita que o listener global envie duas vezes.
      event?.stopPropagation();
      this.onSendReply();
    },
    onEditorKeydown(event) {
      if (
        event?.key !== 'Enter' ||
        event.shiftKey ||
        event.metaKey ||
        event.ctrlKey
      ) {
        return;
      }

      // Fallback nativo: cobre navegadores em que o handleDOMEvents do
      // ProseMirror não é chamado, mantendo Shift/Ctrl/Command como atalho
      // de quebra/ação alternativa.
      this.onEditorSend(event);
    },
    onReplyBoxKeydownCapture(event) {
      if (
        event?.key !== 'Enter' ||
        event.shiftKey ||
        event.metaKey ||
        event.ctrlKey
      ) {
        return;
      }

      const editorElement = event.target?.closest?.('.ProseMirror');
      if (!editorElement || !this.replyEditor?.contains(editorElement)) {
        return;
      }

      if (
        this.showUserMentions ||
        this.showMentions ||
        this.showCannedMenu ||
        this.showVariablesMenu
      ) {
        return;
      }

      event.preventDefault();
      event.stopPropagation();
      this.onSendReply();
    },
    async sendMessageAsMultipleMessages(
      message,
      copilotAcceptedMessage = '',
      preparedMessages = null,
      attachedFilesSnapshot = this.attachedFiles
    ) {
      const messages =
        preparedMessages || this.getMultipleMessagesPayload(message);
      if (!messages.length) return false;

      const isSequentialAttachmentBatch =
        (this.usesMediaGallery ||
          this.isOnWhatsApp ||
          this.hasCannedResponseAttachments) &&
        attachedFilesSnapshot.length > 0;
      this.isSendingAttachments = true;
      this.attachmentSendProgress = {
        current: 0,
        total: messages.length,
        active: messages.length ? 1 : 0,
      };

      try {
        let failedMessageIndex = -1;
        const sentSuccessfully = await messages.reduce(
          (previousSend, messagePayload, index) =>
            previousSend.then(async previousWasSent => {
              if (!previousWasSent) return false;
              this.attachmentSendProgress = {
                current: index,
                total: messages.length,
                active: index + 1,
              };
              const currentWasSent = await this.sendMessage(
                messagePayload,
                messagePayload.message || '',
                copilotAcceptedMessage
              );
              if (currentWasSent) {
                this.attachmentSendProgress.current = index + 1;
              }
              if (!currentWasSent) failedMessageIndex = index;
              return currentWasSent;
            }),
          Promise.resolve(true)
        );

        if (!sentSuccessfully && isSequentialAttachmentBatch) {
          // Devolve para a bandeja tudo o que nao chegou a ser enviado. A
          // mensagem que falhou pode ser a de texto (sem `files`) — nesse caso
          // o corte ainda e o numero de anexos ja entregues, e sem esta
          // restauracao o audio gravado sumia sem chance de tentar de novo.
          const deliveredAttachments =
            failedMessageIndex < 0
              ? 0
              : messages
                  .slice(0, failedMessageIndex)
                  .filter(payload => payload.files?.length).length;
          this.attachedFiles =
            attachedFilesSnapshot.slice(deliveredAttachments);
        }

        return sentSuccessfully;
      } finally {
        this.isSendingAttachments = false;
        this.attachmentSendProgress = { current: 0, total: 0, active: 0 };
      }
    },
    sendMessageAnalyticsData(
      isPrivate,
      { editorMessage = '', copilotAcceptedMessage = '' } = {}
    ) {
      const normalizeForComparison = message => {
        let normalizedMessage = message || '';

        if (this.sendWithSignature && this.messageSignature && !isPrivate) {
          const effectiveChannelType = getEffectiveChannelType(
            this.channelType,
            this.inbox?.medium || ''
          );
          normalizedMessage = removeSignature(
            normalizedMessage,
            this.messageSignature,
            effectiveChannelType
          );
        }

        return trimContent(normalizedMessage);
      };

      const normalizedAcceptedMessage = normalizeForComparison(
        copilotAcceptedMessage
      );
      const normalizedEditorMessage = normalizeForComparison(editorMessage);

      if (normalizedAcceptedMessage && normalizedEditorMessage) {
        useTrack(CAPTAIN_EVENTS.AI_ASSISTED_MESSAGE_SENT, {
          conversationId: this.conversationIdByRoute,
          channelType: this.channelType,
          editedBeforeSend:
            normalizedAcceptedMessage !== normalizedEditorMessage,
          isPrivate,
        });
      }

      // Analytics data for message signature is enabled or not in channels
      return isPrivate
        ? useTrack(CONVERSATION_EVENTS.SENT_PRIVATE_NOTE)
        : useTrack(CONVERSATION_EVENTS.SENT_MESSAGE, {
            channelType: this.channelType,
            signatureEnabled: this.sendWithSignature,
            hasReplyTo: !!this.inReplyTo?.id,
          });
    },
    async onSendReply() {
      const undefinedVariables = getUndefinedVariablesInMessage({
        message: this.message,
        variables: this.messageVariables,
      });
      if (undefinedVariables.length > 0) {
        const undefinedVariablesCount =
          undefinedVariables.length > 1 ? undefinedVariables.length : 1;
        this.undefinedVariableMessage = this.$t(
          'CONVERSATION.REPLYBOX.UNDEFINED_VARIABLES.MESSAGE',
          {
            undefinedVariablesCount,
            undefinedVariables: undefinedVariables.join(', '),
          }
        );

        const ok = await this.$refs.confirmDialog.showConfirmation();
        if (ok) {
          await this.confirmOnSendReply();
        }
      } else {
        await this.confirmOnSendReply();
      }
    },
    async sendMessage(
      messagePayload,
      editorMessage = '',
      copilotAcceptedMessage = ''
    ) {
      if (!messagePayload.private && this.isEditorDisabled) return false;
      try {
        await this.$store.dispatch(
          'createPendingMessageAndSend',
          messagePayload
        );
        emitter.emit(BUS_EVENTS.SCROLL_TO_MESSAGE);
        emitter.emit(BUS_EVENTS.MESSAGE_SENT);
        this.removeFromDraft();
        this.sendMessageAnalyticsData(messagePayload.private, {
          editorMessage,
          copilotAcceptedMessage,
        });
        return true;
      } catch (error) {
        const rawError =
          error?.response?.data?.error || error?.response?.data?.message;
        const errorMessage =
          (typeof rawError === 'string' && rawError) ||
          this.$t('CONVERSATION.MESSAGE_ERROR');
        useAlert(errorMessage, { duration: SEND_ERROR_ALERT_DURATION });
        return false;
      }
    },
    // Aceleres 2026-09-14: a mensagem que falhou (e o audio gravado na bandeja)
    // so existe na memoria desta aba — nunca chegou ao servidor. Medido em
    // homolog: um F5 com a bolha "Falha ao enviar audio" na tela a apagava sem
    // aviso, e nao havia como reenviar. Trocar de conversa pela SPA preserva.
    warnBeforeLosingUnsentMedia(event) {
      const conversations =
        this.$store.state.conversations?.allConversations || [];
      const hasLocalOnlyMessage = conversations.some(conversation =>
        (conversation.messages || []).some(
          message =>
            typeof message.id === 'string' &&
            [MESSAGE_STATUS.FAILED, MESSAGE_STATUS.PROGRESS].includes(
              message.status
            )
        )
      );
      if (
        !hasLocalOnlyMessage &&
        !this.attachedFiles.length &&
        !this.isRecordingAudio &&
        !this.isSendingAttachments
      ) {
        return;
      }
      event.preventDefault();
      // Chrome/Edge antigos so mostram o dialogo com returnValue preenchido.
      event.returnValue = '';
    },
    // Aceleres 2026-08-18: envio de contato. O cartao nao passa pelo editor —
    // ele nao e texto — entao vai direto pelo mesmo `sendMessage` das demais
    // mensagens, herdando a bolha otimista, o horario, os tiques de entrega e o
    // tratamento de erro que ja existem.
    openSendContactModal() {
      this.showSendContactModal = true;
    },
    async onSendContactCard(contactCards) {
      await contactCards.reduce(
        (previousSend, contactCard) =>
          previousSend.then(() =>
            this.sendMessage({
              conversationId: this.currentChat.id,
              private: this.isPrivate,
              sender: this.sender,
              contactCard,
            })
          ),
        Promise.resolve()
      );
    },
    async onSendWhatsAppReply(messagePayload) {
      this.sendMessage({
        conversationId: this.currentChat.id,
        ...messagePayload,
      });
      this.hideWhatsappTemplatesModal();
    },
    async onSendContentTemplateReply(messagePayload) {
      this.sendMessage({
        conversationId: this.currentChat.id,
        ...messagePayload,
      });
      this.hideContentTemplatesModal();
    },
    // Aceleres: handler do item "Notas da conversa" do menu "+" no
    // ReplyBottomPanel — alterna entre Reply e Note.
    onToggleNoteMode() {
      this.setReplyMode(
        this.isOnPrivateNote
          ? REPLY_EDITOR_MODES.REPLY
          : REPLY_EDITOR_MODES.NOTE
      );
    },
    // Unica porta de entrada para a troca manual de modo. O upstream so
    // aplicava o modo quando `can_reply` era verdadeiro (ou o canal era
    // WhatsApp/API): fora da janela de 24h o clique do agente era engolido e
    // ele nao conseguia nem entrar nem sair da nota interna.
    setReplyMode(
      mode = REPLY_EDITOR_MODES.REPLY,
      { recordingFinished = false } = {}
    ) {
      if (
        !recordingFinished &&
        this.isRecordingAudio &&
        this.$refs?.audioRecorderInput
      ) {
        return this.finishRecordingBeforeModeSwitch().then(() =>
          this.setReplyMode(mode, { recordingFinished: true })
        );
      }
      // Aceleres 2026-09-14: trocar entre nota e resposta esvaziava a bandeja
      // inteira, e o audio gravado sumia sem aviso. A regra que o esvaziamento
      // protegia e so a de tipo de arquivo (nota aceita mais tipos que o canal):
      // mantem o que o novo modo aceita e avisa o que precisou sair.
      if (this.attachedFiles.length > 0) {
        this.attachedFiles = this.attachedFiles.filter(attachment => {
          if (attachment?.isRecordedAudio) return true;
          const resource = attachment?.resource || {};
          const name = resource.file?.name || resource.filename || '';
          const isAllowed = isFileTypeAllowedForChannel(
            {
              name,
              type: resource.file?.type || resource.content_type || '',
              size: resource.file?.size || resource.byte_size || 1,
            },
            {
              channelType: this.channelType || this.inbox?.channel_type,
              medium: this.inbox?.medium,
              conversationType: this.conversationType,
              isInstagramChannel: this.isAnInstagramChannel,
              isOnPrivateNote: mode === REPLY_EDITOR_MODES.NOTE,
            }
          );
          if (!isAllowed) {
            useAlert(
              this.$t('CONVERSATION.FILE_TYPE_NOT_SUPPORTED', {
                fileName: name,
              }),
              { duration: SEND_ERROR_ALERT_DURATION }
            );
          }
          return isAllowed;
        });
      }

      this.$store.dispatch('draftMessages/setReplyEditorMode', {
        mode,
      });
      this.replyType = mode;
      if (this.isRecordingAudio) {
        this.toggleAudioRecorder();
      }
    },
    // O gravador so existe no modo resposta: trocar de modo com a gravacao em
    // curso desmontava o componente e descartava o audio. Finaliza antes, para
    // o audio cair na bandeja, e so entao troca (ate 5s; falhando, segue o
    // descarte antigo no fim de setReplyMode).
    finishRecordingBeforeModeSwitch() {
      return new Promise(resolve => {
        let stopWatching = () => {};
        const timeout = setTimeout(() => {
          stopWatching();
          resolve();
        }, 5000);
        stopWatching = this.$watch('isRecordingAudio', isRecording => {
          if (isRecording) return;
          clearTimeout(timeout);
          stopWatching();
          resolve();
        });
        this.finishAudioRecording();
      });
    },
    // Troca de modo automatica e legitima (troca de conversa): mantem a store
    // em dia sem disparar o vaivem de rascunho do watcher de replyType.
    applyReplyModeWithoutDraftSync(mode) {
      if (this.replyType === mode) return;
      this.skipReplyTypeDraftSync = true;
      this.replyType = mode;
      this.$store.dispatch('draftMessages/setReplyEditorMode', { mode });
    },
    clearEditorSelection() {
      this.updateEditorSelectionWith = '';
    },
    addIntoEditor(content) {
      this.updateEditorSelectionWith = content;
      this.onFocus();
      // Aceleres: o editor se refoca ao inserir (Editor.vue chama
      // editorView.focus()). Com a folha de emojis aberta no mobile isso
      // traria o teclado virtual de volta por cima dela a cada toque, então
      // devolvemos o foco depois do flush. A seleção do ProseMirror
      // sobrevive ao blur, então os emojis seguintes continuam entrando na
      // mesma posição.
      if (this.showEmojiPicker && this.isMobileViewport) {
        this.$nextTick(() => {
          if (this.showEmojiPicker) document.activeElement?.blur?.();
        });
      }
    },
    executeCopilotAction(action, data) {
      this.copilot.execute(action, data);
    },
    clearMessage() {
      this.message = '';
      this.clearCopilotAcceptedMessage();
      if (this.sendWithSignature && !this.isPrivate) {
        // if signature is enabled, append it to the message
        const effectiveChannelType = getEffectiveChannelType(
          this.channelType,
          this.inbox?.medium || ''
        );
        this.message = appendSignature(
          this.message,
          this.messageSignature,
          effectiveChannelType
        );
      }
      this.attachedFiles = [];
      this.isRecordingAudio = false;
      this.resetReplyToMessage();
      this.resetAudioRecorderInput();
    },
    clearEmailField() {
      this.ccEmails = '';
      this.bccEmails = '';
      this.toEmails = '';
    },

    getEmojiPickerTrigger() {
      return this.replyEditor?.querySelector('.acl-composer-emoji');
    },
    refreshEmojiPickerPosition() {
      if (this.showEmojiPicker) recomputeAllPoppers();
    },
    // Pedido pelos "+" das reacoes rapidas (Message.vue, menu da mensagem).
    // Com `onSelect` o emoji volta direto a quem pediu; so com `messageId` volta
    // pelo `REACTION_EMOJI_SELECTED`, que o Message.vue trata.
    onOpenReactionEmojiSheet(request = {}) {
      if (!request.messageId && !request.onSelect) return;
      this.hideEmojiPicker();
      this.reactionSheetRequest = request;
    },
    onReactionSheetSelect(emoji) {
      const { messageId, onSelect } = this.reactionSheetRequest;
      this.reactionSheetRequest = null;
      if (onSelect) {
        onSelect(emoji);
        return;
      }
      emitter.emit(BUS_EVENTS.REACTION_EMOJI_SELECTED, { messageId, emoji });
    },
    onEmojiSheetBackspace() {
      this.$refs.messageEditor?.deleteBackward?.();
    },
    toggleEmojiPicker() {
      const shouldOpen = !this.showEmojiPicker;
      if (shouldOpen) {
        emitter.emit(BUS_EVENTS.EMOJI_PICKER_OPENED, { owner: 'reply' });
        // Aceleres: no mobile a folha ocupa o mesmo espaço do teclado
        // virtual. Sem tirar o foco do editor os dois disputam a tela e a
        // conversa fica espremida — mesmo cuidado do popover de respostas
        // rápidas.
        if (
          window.matchMedia('(max-width: 767px), (pointer: coarse)').matches
        ) {
          document.activeElement?.blur?.();
        }
      }
      if (!shouldOpen) this.resetEmojiSheetHeight();
      this.showEmojiPicker = shouldOpen;
      this.suppressWhatsAppGallery = shouldOpen;
      if (shouldOpen) {
        this.$nextTick(this.refreshEmojiPickerPosition);
      }
    },
    openMediaPicker() {
      this.showEmojiPicker = false;
      this.suppressWhatsAppGallery = false;
    },
    prepareMediaUpload(file) {
      this.openMediaPicker();
      return this.onFileUpload(normalizeUploadedAudioFile(file));
    },
    aceleresApiBase() {
      return resolveAceleresApiBase();
    },
    gifConversationPayload() {
      const chat = this.currentChat || {};
      const sender = chat.meta?.sender || {};
      const attributeSources = [
        chat.additional_attributes,
        chat.custom_attributes,
        sender.additional_attributes,
        sender.custom_attributes,
      ];
      const remoteJid =
        attributeSources
          .map(attributes => attributes?.remote_jid || attributes?.remoteJid)
          .find(Boolean) ||
        sender.identifier ||
        sender.phone_number ||
        '';

      return {
        chatwoot_account_id: parseInt(
          this.$store.getters.getCurrentAccountId,
          10
        ),
        chatwoot_conversation_id: parseInt(
          chat.id ||
            this.$route?.params?.conversationId ||
            this.$route?.params?.conversation_id ||
            this.$route?.params?.id,
          10
        ),
        chatwoot_inbox_id:
          parseInt(chat.inbox_id || this.inbox?.id, 10) || null,
        // É apenas uma dica para compatibilidade. A API resolve o JID
        // canônico no servidor a partir da conversa autorizada.
        remote_jid: remoteJid ? String(remoteJid) : null,
      };
    },
    async fetchGifRequest(url, options, timeoutMessage) {
      const controller = new AbortController();
      const timeoutId = window.setTimeout(() => controller.abort(), 45000);

      try {
        return await fetch(url, { ...options, signal: controller.signal });
      } catch (error) {
        if (error?.name === 'AbortError') {
          throw new Error(timeoutMessage);
        }

        throw error;
      } finally {
        window.clearTimeout(timeoutId);
      }
    },
    async onGifSelect(gif) {
      if (this.isSendingGif) {
        useAlert('Uma figurinha já está sendo enviada. Aguarde a conclusão.');
        return false;
      }

      // Fecha o picker antes de iniciar a leitura do arquivo.
      this.isSendingGif = true;
      this.showEmojiPicker = false;
      try {
        if (!gif || !this.currentChat?.id) {
          useAlert('Não foi possível identificar a conversa.');
          return false;
        }
        const file = await this.stickerFileFromLibrary(gif);
        const sent = await this.sendStickerAsChatwootMessage(
          file,
          String(this.message || '').trim(),
          gif
        );
        if (sent) {
          this.message = '';
          this.resetReplyToMessage();
        }
        return sent;
      } catch (error) {
        useAlert(
          error?.message || 'Erro ao enviar figurinha. Tente novamente.'
        );
        return false;
      } finally {
        this.isSendingGif = false;
      }
    },
    stickerBinaryUrl(gif) {
      // Figurinha guardada pelo próprio atendente ("Adicionar para mim"): o
      // ficheiro é um blob do próprio Chatwoot, não há registo na biblioteca
      // do Aceleres nem id de servidor — lê-se o URL direto, mesma origem.
      if (gif?.saved_url) return gif.saved_url;
      const id = gif?.id;
      return id ? `${this.aceleresApiBase()}/api/stickers/${id}/binary` : '';
    },
    async stickerFileFromLibrary(gif) {
      const url = this.stickerBinaryUrl(gif);
      if (!url) {
        throw new Error(
          'Não foi possível identificar a figurinha selecionada.'
        );
      }
      if (gif?.lottie_url) {
        const blob = await renderLottieStickerFrame(gif.lottie_url);
        return new File([blob], 'figurinha.png', { type: 'image/png' });
      }
      // O arquivo também é servido por /storage, mas aquele caminho sai pelo
      // nginx sem cabeçalho CORS — e aqui é `fetch`, não `<img>`.
      const response = await this.fetchGifRequest(
        url,
        {
          headers: {
            'X-Chatwoot-Token': this.currentUser?.access_token || '',
          },
        },
        'A leitura da figurinha demorou demais. Tente novamente.'
      );
      if (!response.ok) {
        throw new Error('Não foi possível ler a figurinha selecionada.');
      }
      const blob = await response.blob();
      const type = blob.type || 'image/gif';
      const extension = (type.split('/')[1] || 'gif').replace(
        /[^a-z0-9]/gi,
        ''
      );
      const baseName = String(gif?.title || 'figurinha').replace(
        /[^\w-]+/g,
        '-'
      );
      return new File([blob], `${baseName}.${extension}`, { type });
    },
    /**
     * A figurinha passa pelo compositor do próprio Chatwoot: o balão aparece
     * na hora pelo render otimista, a autoria é a nativa (sem depender de
     * `wame_author_chatwoot_user_id`) e o eco da WAME é descartado por
     * `SyncWameMessageToChatwoot::isOutboundEcho`. O marcador
     * `aceleres_sticker` faz o Laravel usar o endpoint de sticker no lugar do
     * de imagem, e `wame_media_type` preserva o visual de figurinha na bolha.
     */
    async sendStickerAsChatwootMessage(file, caption, librarySticker = {}) {
      const libraryId = Number(librarySticker?.id);
      const libraryScopes = Array.isArray(librarySticker?.scopes)
        ? librarySticker.scopes
        : [librarySticker?.scope].filter(Boolean);
      let messagePayload = {
        conversationId: this.currentChat.id,
        message: caption || '',
        private: this.isPrivate,
        sender: this.sender,
        files: [file],
        contentAttributes: {
          aceleres_sticker: true,
          wame_media_type: 'sticker',
          ...(Number.isInteger(libraryId) && libraryId > 0
            ? { aceleres_sticker_library_id: libraryId }
            : {}),
          ...(librarySticker?.content_hash
            ? {
                aceleres_sticker_content_hash: librarySticker.content_hash,
              }
            : {}),
          ...(libraryScopes.length
            ? { aceleres_sticker_library_scopes: libraryScopes }
            : {}),
          ...(librarySticker?.saved_url
            ? { aceleres_sticker_saved_for_user: true }
            : {}),
        },
      };
      messagePayload = this.setReplyToInPayload(messagePayload);
      return this.sendMessage(messagePayload);
    },
    isGifAttachment(attachment) {
      const file = attachment?.resource?.file;
      const type = file?.type || attachment?.resource?.content_type || '';
      const name = file?.name || attachment?.resource?.filename || '';
      return type.toLowerCase() === 'image/gif' || /\.gif$/i.test(name);
    },
    async gifFileFromAttachment(attachment) {
      const originalFile = attachment?.resource?.file;
      if (originalFile instanceof File) return originalFile;

      if (!attachment?.thumb) {
        throw new Error('Não foi possível ler o GIF-fonte da figurinha.');
      }
      const response = await fetch(attachment.thumb);
      if (!response.ok)
        throw new Error('Não foi possível ler o GIF-fonte da figurinha.');
      const blob = await response.blob();
      const name = attachment?.resource?.filename || 'gif-anexado.gif';
      return new File([blob], name, { type: 'image/gif' });
    },
    async sendAttachedGifsDirectly() {
      const context = this.gifConversationPayload();
      if (!context.chatwoot_account_id || !context.chatwoot_conversation_id) {
        useAlert('Não foi possível identificar a conversa.');
        return false;
      }

      this.isSendingAttachments = true;
      this.attachmentSendProgress = {
        current: 0,
        total: this.attachedFiles.length,
        active: 1,
      };
      try {
        for (let index = 0; index < this.attachedFiles.length; index += 1) {
          const attachment = this.attachedFiles[index];
          const file = await this.gifFileFromAttachment(attachment);
          const formData = new FormData();
          formData.append('gif', file);
          formData.append('scope', 'user');
          formData.append('name', file.name.replace(/\.gif$/i, ''));
          formData.append(
            'chatwoot_account_id',
            String(context.chatwoot_account_id)
          );
          formData.append(
            'chatwoot_conversation_id',
            String(context.chatwoot_conversation_id)
          );
          if (context.chatwoot_inbox_id) {
            formData.append(
              'chatwoot_inbox_id',
              String(context.chatwoot_inbox_id)
            );
          }
          // Os uploads são seriais para preservar a legenda na primeira figurinha.
          // eslint-disable-next-line no-await-in-loop
          const upload = await this.fetchGifRequest(
            `${this.aceleresApiBase()}/api/stickers`,
            {
              method: 'POST',
              headers: {
                Accept: 'application/json',
                'X-Chatwoot-Token': this.currentUser?.access_token || '',
              },
              body: formData,
            },
            'O upload da figurinha demorou demais. Tente novamente.'
          );
          const uploaded = await upload.json().catch(() => ({}));
          if (
            !upload.ok ||
            uploaded?.success === false ||
            !uploaded?.data?.id
          ) {
            throw new Error(
              uploaded?.message || 'Não foi possível preparar a figurinha.'
            );
          }

          // O upload acima mantém a figurinha na biblioteca do atendente; o
          // envio em si vai pelo compositor do Chatwoot, com o arquivo local
          // que já está em mãos.
          // eslint-disable-next-line no-await-in-loop
          const sent = await this.sendStickerAsChatwootMessage(
            file,
            String(
              attachment.caption || (index === 0 ? this.message : '') || ''
            ).trim()
          );
          if (!sent) {
            throw new Error('Não foi possível enviar a figurinha.');
          }
          this.attachmentSendProgress.current = index + 1;
          this.attachmentSendProgress.active = index + 1;
        }
        this.attachedFiles = [];
        this.message = '';
        return true;
      } catch (error) {
        useAlert(
          error?.message || 'Não foi possível enviar a figurinha ao WhatsApp.'
        );
        return false;
      } finally {
        this.isSendingAttachments = false;
        this.attachmentSendProgress = { current: 0, total: 0, active: 0 };
      }
    },
    toggleAudioRecorder() {
      this.isRecordingAudio = !this.isRecordingAudio;
      this.toggleTyping(
        this.isRecordingAudio ? 'recording_on' : 'recording_off'
      );
      if (!this.isRecordingAudio) {
        this.resetAudioRecorderInput();
      }
    },
    toggleAudioRecorderPlayPause() {
      if (!this.$refs.audioRecorderInput) return;
      // Áudio já finalizado → controla playback (ouvir antes de enviar)
      if (this.hasRecordedAudio) {
        this.$refs.audioRecorderInput.playPause();
        return;
      }
      // Gravando: alterna PAUSA/CONTINUA sem finalizar nem anexar
      if (this.isRecordingPaused) {
        this.$refs.audioRecorderInput.resumeRecording();
        this.toggleTyping('recording_on');
        this.isRecordingPaused = false;
        this.recordingAudioState = '';
      } else {
        this.$refs.audioRecorderInput.pauseRecording();
        this.toggleTyping('recording_off');
        this.isRecordingPaused = true;
        this.recordingAudioState = 'recording-paused';
      }
    },
    // Finaliza a gravação e anexa (NÃO envia — fica pronto pra revisar/enviar)
    finishAudioRecording() {
      if (!this.$refs.audioRecorderInput) return;
      this.toggleTyping('recording_off');
      this.$refs.audioRecorderInput.stopRecording();
      this.isRecordingPaused = false;
    },
    // Aceleres 2026-07-03: botão Enviar durante a gravação (WhatsApp) —
    // finaliza e envia em seguida. Seta a flag; o watcher em attachedFiles
    // chama onSendReply assim que o áudio finalizado entra na bandeja (o
    // attach via finishRecord/onFileUpload é assíncrono).
    sendAudioRecording() {
      if (!this.$refs.audioRecorderInput) return;
      this.toggleTyping('recording_off');
      this.sendOnFinish = true;
      this.$refs.audioRecorderInput.stopRecording();
      this.isRecordingPaused = false;
    },
    // Apaga a gravação atual sem anexar nem enviar
    deleteAudioRecording() {
      this.toggleTyping('recording_off');
      this.isRecordingPaused = false;
      // Aceleres: no telemóvel, ao descartar o áudio o gravador sai e o editor
      // volta a montar com `focus-on-mount`, abrindo o teclado. Mesmo par
      // (flag + blur) usado no envio. No computador o foco continua.
      const suprimirFoco = this.isMobileViewport;
      if (suprimirFoco) this.suppressEditorFocusOnMount = true;

      this.resetAudioRecorderInput();

      if (suprimirFoco) {
        document.activeElement?.blur?.();
        this.$nextTick(() => {
          document.activeElement?.blur?.();
          this.suppressEditorFocusOnMount = false;
        });
      }
    },
    // Aceleres: microfone inacessível (permissão negada / iframe / WebView).
    // Mostra o motivo e reseta o estado de gravação p/ o composer voltar ao normal.
    onRecordError(message) {
      useAlert(message || 'Não foi possível acessar o microfone.');
      this.resetAudioRecorderInput();
    },
    // Aceleres: em telas de toque o vue3-click-away escuta `touchstart`, que
    // o `@click.stop` do botão de emoji não barra. Sem esta guarda o toque no
    // botão fecharia a folha pelo clickaway e o clique seguinte a reabriria,
    // deixando o botão sem efeito. Ignoramos eventos nascidos no próprio
    // gatilho e deixamos o toggle decidir.
    // Aceleres: arrasto da folha de emojis no mobile. A pegada usa Pointer
    // Events com `setPointerCapture`, então o dedo continua sendo seguido
    // mesmo saindo do puxador, sem precisar de listeners no document. O
    // `touch-action: none` do puxador é o que impede o navegador de rolar a
    // página em vez de arrastar.
    emojiSheetMaxHeight() {
      return Math.round((window.innerHeight || 640) * 0.72);
    },
    onEmojiSheetDragStart(event) {
      this.emojiSheetDragStartY = event.clientY;
      this.emojiSheetDragStartHeight =
        this.emojiSheetHeight || this.$refs.emojiSheet?.offsetHeight || 0;
      if (!this.emojiSheetDragStartHeight) return;

      this.emojiSheetDragging = true;
      this.emojiSheetDragMoved = false;
      event.currentTarget?.setPointerCapture?.(event.pointerId);
    },
    onEmojiSheetDragMove(event) {
      if (!this.emojiSheetDragging) return;

      // Arrastar para cima cresce a folha; para baixo, encolhe. Pode chegar a
      // zero: é assim que dá para levá-la até o rodapé da tela.
      const delta = this.emojiSheetDragStartY - event.clientY;
      const height = this.emojiSheetDragStartHeight + delta;

      this.emojiSheetDragMoved = true;
      this.emojiSheetHeight = Math.min(
        Math.max(height, 0),
        this.emojiSheetMaxHeight()
      );
    },
    onEmojiSheetDragEnd() {
      if (!this.emojiSheetDragging) return;
      this.emojiSheetDragging = false;

      // Um toque no puxador não fecha nada — sem arrasto, nada muda.
      if (!this.emojiSheetDragMoved) return;

      // Encostou no rodapé: fecha. Em qualquer outra altura a folha só se
      // move, voltando ao mínimo utilizável se o gesto passou dele.
      if (this.emojiSheetHeight <= EMOJI_SHEET_DISMISS_HEIGHT) {
        this.resetEmojiSheetHeight();
        this.hideEmojiPicker();
        return;
      }

      if (this.emojiSheetHeight < EMOJI_SHEET_MIN_HEIGHT) {
        this.emojiSheetHeight = EMOJI_SHEET_MIN_HEIGHT;
      }
    },
    resetEmojiSheetHeight() {
      this.emojiSheetHeight = 0;
      this.emojiSheetDragging = false;
      this.emojiSheetDragMoved = false;
    },
    onEmojiSheetClickAway(event) {
      const trigger = this.getEmojiPickerTrigger();
      if (trigger && event?.target && trigger.contains(event.target)) return;
      this.hideEmojiPicker();
    },
    hideEmojiPicker() {
      if (this.showEmojiPicker) {
        this.toggleEmojiPicker();
      } else {
        this.suppressWhatsAppGallery = false;
      }
    },
    onEmojiPickerOpened({ owner } = {}) {
      if (owner === 'reply') return;
      this.resetEmojiSheetHeight();
      this.showEmojiPicker = false;
      this.suppressWhatsAppGallery = false;
    },
    onTypingOn() {
      this.toggleTyping('on');
    },
    onTypingOff() {
      this.toggleTyping('off');
    },
    onBlur() {
      this.isFocused = false;
      this.clearReplyBoxDraftHeight();
      this.saveDraft(this.conversationIdByRoute, this.replyType);
    },
    closeCopilotEditor() {
      const shouldSuppressFocus = this.isCopilotMobileViewport;
      if (shouldSuppressFocus) {
        this.suppressEditorFocusOnMount = true;
        document.activeElement?.blur?.();
      }

      this.copilot.reset();
    },
    onReplyEditorTransitionAfterEnter() {
      this.suppressEditorFocusOnMount = false;
    },
    onFocus() {
      this.isFocused = true;
      this.syncReplyBoxDraftHeight();
    },
    onRecordProgressChanged(duration) {
      this.recordingAudioDurationText = duration;
    },
    onFinishRecorder(file) {
      this.recordingAudioState = 'stopped';
      this.hasRecordedAudio = true;
      // Added a new key isRecordedAudio to the file to find it's and recorded audio
      // Because to filter and show only non recorded audio and other attachments
      const autoRecordedFile = {
        ...file,
        isRecordedAudio: true,
      };
      const result = file && this.onFileUpload(autoRecordedFile);
      // Aceleres: desmonta a faixa de gravação (o áudio anexado passa a ser
      // preview/enviado pela bandeja de anexos — fluxo "melhoria leve"
      // estilo ChatGPT, sem takeover do composer).
      this.isRecordingAudio = false;
      return result;
    },
    toggleTyping(status) {
      const conversationId = this.currentChat.id;
      const isPrivate = this.isPrivate;

      if (!conversationId) {
        return;
      }

      this.$store.dispatch('conversationTypingStatus/toggleTyping', {
        status,
        conversationId,
        isPrivate,
      });
    },
    attachFile({ blob, file }) {
      const sourceFile = file?.file || file;
      if (!sourceFile) return;

      const reader = new FileReader();
      reader.readAsDataURL(sourceFile);
      reader.onloadend = async () => {
        const isGifMedia = await looksLikeGif(sourceFile);
        this.releaseAttachmentUploadSlot();
        const isFirstFile = !this.attachedFiles.some(f => !f?.isRecordedAudio);
        let initialCaption = '';
        if (
          isFirstFile &&
          !this.isPrivate &&
          !file?.isRecordedAudio &&
          this.message &&
          this.message.trim()
        ) {
          // O editor fica oculto ao anexar o primeiro arquivo público.
          // Mantém o rascunho na legenda da prévia para não perdê-lo.
          initialCaption = this.message;
          this.message = '';
        }
        const previewId = isPdfFile(sourceFile)
          ? `pdf-preview-${Date.now()}-${Math.random()}`
          : null;
        this.attachedFiles.push({
          currentChatId: this.currentChat.id,
          resource: blob || file,
          isPrivate: this.isPrivate,
          thumb: reader.result,
          originalFileUrl: previewId ? reader.result : null,
          previewId,
          isPdfPreview: Boolean(previewId),
          isPdfPreviewLoading: Boolean(previewId),
          blobSignedId: blob ? blob.signed_id : undefined,
          isRecordedAudio: file?.isRecordedAudio || false,
          isUploadedAudio:
            !file?.isRecordedAudio &&
            isAudioAttachment({
              fileType: sourceFile.type.startsWith('audio/') ? 'audio' : '',
              contentType: sourceFile.type,
              fileName: sourceFile.name,
            }),
          isGifMedia,
          caption: initialCaption,
        });
        if (previewId) {
          this.loadPdfFirstPagePreview(sourceFile, previewId);
        }
        // Aceleres 2026-07-03 (fix): botão Enviar da gravação (WhatsApp).
        // sendAudioRecording setou sendOnFinish antes do stopRecording; a
        // cadeia convertAudio → finishRecord → onFileUpload termina AQUI,
        // logo após o arquivo cair na bandeja. Disparamos o envio neste ponto
        // (e não num watcher em attachedFiles, que em Vue 3 não dispara no
        // push — ver watch{}). O blob direto já foi upado neste momento.
        if (this.sendOnFinish) {
          this.sendOnFinish = false;
          this.$nextTick(() => this.onSendReply());
        }
      };
    },
    async loadPdfFirstPagePreview(file, previewId) {
      try {
        const previewResult = await createPdfFirstPagePreview(file);
        if (!previewResult?.preview || this.$?.isUnmounted) return;

        const attachmentIndex = this.attachedFiles.findIndex(
          attachment => attachment.previewId === previewId
        );
        if (attachmentIndex < 0) return;

        const attachment = this.attachedFiles[attachmentIndex];
        this.attachedFiles.splice(attachmentIndex, 1, {
          ...attachment,
          thumb: previewResult.preview,
          pdfPageCount: previewResult.pageCount,
          isPdfPreviewLoading: false,
        });
      } catch {
        if (this.$?.isUnmounted) return;

        const attachmentIndex = this.attachedFiles.findIndex(
          attachment => attachment.previewId === previewId
        );
        if (attachmentIndex < 0) return;

        const attachment = this.attachedFiles[attachmentIndex];
        this.attachedFiles.splice(attachmentIndex, 1, {
          ...attachment,
          isPdfPreviewLoading: false,
        });
      }
    },
    removeAttachment(attachments) {
      const isLeavingGallery =
        this.showWhatsAppGallery &&
        !attachments.some(attachment => !attachment?.isRecordedAudio);
      if (isLeavingGallery && this.isMobileViewport) {
        this.suppressEditorFocusOnMount = true;
        document.activeElement?.blur?.();
      }

      this.attachedFiles = attachments;

      if (isLeavingGallery && this.isMobileViewport) {
        this.$nextTick(() => {
          document.activeElement?.blur?.();
          this.suppressEditorFocusOnMount = false;
        });
      }
    },
    handleCannedAttachment(payload) {
      if (!payload.attachmentUrl || !payload.attachmentSignedId) return;
      const urlFileName = payload.attachmentUrl.split('?')[0].split('/').pop();
      const fname =
        payload.fileName || decodeURIComponent(urlFileName) || 'attachment';
      const ext = fname.includes('.')
        ? fname.split('.').pop().toLowerCase()
        : '';
      const imgExts = ['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg'];
      const videoExts = ['mp4', 'webm', 'mov', 'm4v'];
      const audExts = ['mp3', 'wav', 'ogg', 'm4a', 'aac'];
      let mime = payload.fileType || 'application/octet-stream';
      if (!payload.fileType && imgExts.includes(ext)) {
        mime = 'image/' + ext;
      } else if (!payload.fileType && videoExts.includes(ext)) {
        mime = 'video/' + ext;
      } else if (!payload.fileType && audExts.includes(ext)) {
        mime = 'audio/' + ext;
      }
      this.attachedFiles.push({
        currentChatId: this.currentChat.id,
        resource: {
          content_type: mime,
          filename: fname,
          byte_size: Number(payload.fileSize) || 0,
        },
        isPrivate: this.isOnPrivateNote,
        thumb: payload.attachmentUrl,
        blobSignedId: payload.attachmentSignedId,
        isRecordedAudio: false,
        isCannedResponseAttachment: true,
        caption: payload.caption || '',
      });
    },
    updateAttachmentCaption({ index, caption }) {
      const nonRecorded = this.attachedFiles.filter(f => !f?.isRecordedAudio);
      const target = nonRecorded[index];
      if (!target) return;
      const realIndex = this.attachedFiles.indexOf(target);
      if (realIndex < 0) return;
      const updated = { ...this.attachedFiles[realIndex], caption };
      this.attachedFiles.splice(realIndex, 1, updated);
    },
    // Resolve what to hand the backend for an attached file.
    // - Direct uploads on: the ActiveStorage blob signed id.
    // - Pre-uploaded attachments (e.g. canned responses) carry a blobSignedId but no
    //   raw File in resource; MessageBuilder accepts the signed id directly, so reuse it
    //   even when direct uploads are off (otherwise the file would be undefined and the
    //   attachment never reaches the customer).
    // - Otherwise: the raw File object for multipart upload.
    getAttachableFile(attachment) {
      if (this.globalConfig.directUploadsEnabled) {
        return attachment.blobSignedId || attachment.resource.file;
      }
      if (attachment.blobSignedId && !attachment.resource?.file) {
        return attachment.blobSignedId;
      }
      return attachment.resource.file;
    },
    /**
     * Aceleres: sem esta marca a bolha nao tem como distinguir GIF de video.
     * A interface decide isso por um unico criterio — `wame_media_type` —, e
     * ele so era escrito no caminho de entrada (WAME -> Laravel -> Chatwoot).
     * Mensagem nascida no painel chegava sem nada e caia na bolha de video,
     * com controles e duracao. Aqui o compositor declara a intencao, do mesmo
     * jeito que ja faz com a figurinha.
     */
    setGifMarkerInPayload(payload, attachment) {
      if (!attachment?.isGifMedia) return payload;

      return {
        ...payload,
        contentAttributes: {
          ...payload.contentAttributes,
          wame_media_type: 'gif',
        },
      };
    },
    setUploadedAudioMarkerInPayload(payload, attachment) {
      if (!attachment?.isUploadedAudio) return payload;

      return {
        ...payload,
        contentAttributes: {
          ...payload.contentAttributes,
          wame_media_type: 'audio',
          aceleres_uploaded_audio: true,
        },
      };
    },
    setReplyToInPayload(payload) {
      if (this.inReplyTo?.id) {
        return {
          ...payload,
          contentAttributes: {
            ...payload.contentAttributes,
            in_reply_to: this.inReplyTo.id,
          },
        };
      }

      return payload;
    },
    getMultipleMessagesPayload(message) {
      const multipleMessagePayload = [];

      if (this.attachedFiles && this.attachedFiles.length) {
        const usesMediaGallery =
          this.usesMediaGallery ||
          ((this.isOnWhatsApp || this.hasCannedResponseAttachments) &&
            !this.isPrivate);
        let fallbackCaption = this.splitsTextAndAttachments ? '' : message;
        this.attachedFiles.forEach((attachment, index) => {
          const attachedFile = this.getAttachableFile(attachment);
          let perAttachmentCaption;
          if (usesMediaGallery) {
            // Cada arquivo usa a própria legenda, editada na prévia.
            // Respostas prontas antigas ou pré-enviadas podem chegar sem
            // legenda no blob. Com um único anexo, o texto do editor pertence
            // a esse arquivo e deve seguir na mesma mensagem.
            perAttachmentCaption =
              attachment.caption ||
              (this.hasCannedResponseAttachments &&
              this.attachedFiles.length === 1 &&
              index === 0
                ? message
                : '');
          } else {
            perAttachmentCaption = fallbackCaption;
          }
          let attachmentPayload = {
            conversationId: this.currentChat.id,
            files: [attachedFile],
            // Era `false` fixo porque este caminho so existia para o envio
            // publico. Agora a nota tambem passa por aqui, e o destino tem de
            // vir do mesmo sinal que o envio de mensagem unica ja usa.
            private: this.isPrivate,
            message: perAttachmentCaption,
            sender: this.sender,
          };

          attachmentPayload = this.setReplyToInPayload(attachmentPayload);
          attachmentPayload = this.setGifMarkerInPayload(
            attachmentPayload,
            attachment
          );
          attachmentPayload = this.setUploadedAudioMarkerInPayload(
            attachmentPayload,
            attachment
          );
          multipleMessagePayload.push(attachmentPayload);
          // Legacy path (non-WhatsApp): only the first attachment gets the caption
          if (!usesMediaGallery && !this.splitsTextAndAttachments) {
            fallbackCaption = '';
          }
        });
      }

      const hasNoAttachments =
        !this.attachedFiles || !this.attachedFiles.length;
      // Instagram, Messenger e TikTok: o texto vai sempre como mensagem
      // separada (nao ha legenda no anexo). No WhatsApp so ha mensagem de
      // texto quando nao ha anexo nenhum.
      if (
        (this.splitsTextAndAttachments && this.message) ||
        (!this.splitsTextAndAttachments && hasNoAttachments)
      ) {
        let messagePayload = {
          conversationId: this.currentChat.id,
          message,
          private: this.isPrivate,
          sender: this.sender,
        };

        messagePayload = this.setReplyToInPayload(messagePayload);

        multipleMessagePayload.push(messagePayload);
      }

      return multipleMessagePayload;
    },
    getMessagePayload(message) {
      const messageWithQuote = this.getMessageWithQuotedEmailText(message);

      let messagePayload = {
        conversationId: this.currentChat.id,
        message: messageWithQuote,
        private: this.isPrivate,
        sender: this.sender,
      };
      messagePayload = this.setReplyToInPayload(messagePayload);

      if (this.attachedFiles && this.attachedFiles.length) {
        messagePayload.files = [];
        this.attachedFiles.forEach(attachment => {
          messagePayload.files.push(this.getAttachableFile(attachment));
        });
        // Este caminho junta todos os anexos numa mensagem só, então a marca
        // de GIF só faz sentido quando há um único anexo para descrever.
        if (this.attachedFiles.length === 1) {
          messagePayload = this.setGifMarkerInPayload(
            messagePayload,
            this.attachedFiles[0]
          );
          messagePayload = this.setUploadedAudioMarkerInPayload(
            messagePayload,
            this.attachedFiles[0]
          );
        }
      }

      if (this.ccEmails && !this.isOnPrivateNote) {
        messagePayload.ccEmails = this.ccEmails;
      }

      if (this.bccEmails && !this.isOnPrivateNote) {
        messagePayload.bccEmails = this.bccEmails;
      }

      if (this.toEmails && !this.isOnPrivateNote) {
        messagePayload.toEmails = this.toEmails;
      }
      return messagePayload;
    },
    setCcEmails(value) {
      this.bccEmails = value.bccEmails;
      this.ccEmails = value.ccEmails;
    },
    setCCAndToEmailsFromLastChat() {
      const conversationContact = this.currentChat?.meta?.sender?.email || '';
      const { email: inboxEmail, forward_to_email: forwardToEmail } =
        this.inbox;

      const { cc, bcc, to } = getRecipients(
        this.lastEmail,
        conversationContact,
        inboxEmail,
        forwardToEmail
      );

      this.toEmails = to.join(', ');
      this.ccEmails = cc.join(', ');
      this.bccEmails = bcc.join(', ');
    },
    fetchAndSetReplyTo(replyMessage = this.inReplyTo) {
      const replyStorageKey = LOCAL_STORAGE_KEYS.MESSAGE_REPLY_TO;
      const replyToMessageId = LocalStorage.getFromJsonStore(
        replyStorageKey,
        this.conversationId
      );

      const loadedMessage = this.currentChat?.messages?.find(
        message => String(message.id) === String(replyToMessageId)
      );
      const replyConversationId =
        replyMessage?.conversation_id ?? replyMessage?.conversationId;
      const isCurrentReply =
        replyToMessageId &&
        String(replyMessage?.id) === String(replyToMessageId) &&
        String(replyConversationId) === String(this.conversationId);

      this.inReplyTo = loadedMessage || (isCurrentReply ? replyMessage : undefined);
    },
    onReplyToMessage(message) {
      this.fetchAndSetReplyTo(message);
      if (this.inReplyTo) {
        this.$nextTick(() => {
          const pos = this.isSignatureEnabledForInbox ? 'start' : 'end';
          this.messageEditor?.focusEditorInputField(pos);
        });
      }
    },
    resetReplyToMessage() {
      const replyStorageKey = LOCAL_STORAGE_KEYS.MESSAGE_REPLY_TO;
      LocalStorage.deleteFromJsonStore(replyStorageKey, this.conversationId);
      emitter.emit(BUS_EVENTS.TOGGLE_REPLY_TO_MESSAGE);
    },
    onNewConversationModalActive(isActive) {
      // Issue is if the new conversation modal is open and we drag and drop the file
      // then the file is not getting attached to the new conversation modal
      // and it is getting attached to the current conversation reply box
      // so to fix this we are removing the drag and drop event listener from the current conversation reply box
      // When new conversation modal is open
      this.newConversationModalActive = isActive;
    },
    onSearchPopoverClose() {
      this.showArticleSearchPopover = false;
    },
    toggleInsertArticle() {
      this.showArticleSearchPopover = !this.showArticleSearchPopover;
    },
    resetAudioRecorderInput() {
      this.recordingAudioDurationText = '00:00';
      this.isRecordingAudio = false;
      this.recordingAudioState = '';
      this.isRecordingPaused = false;
      this.hasRecordedAudio = false;
      // Aceleres 2026-07-03 (fix): limpa a flag de envio-pós-gravação nos
      // caminhos de aborto (erro no mic/convertAudio, descartar, toggle off).
      // Sem isso, uma falha no finish deixaria sendOnFinish=true e o próximo
      // anexo normal seria enviado automaticamente pelo gatilho em attachFile.
      this.sendOnFinish = false;
      // Only clear the recorded audio when we click toggle button.
      this.attachedFiles = this.attachedFiles.filter(
        file => !file?.isRecordedAudio
      );
    },
    toggleEditorSize() {
      this.$emit('toggleEditorSize');
      this.$nextTick(() => this.messageEditor?.focusEditorInputField());
    },
    onSubmitCopilotReply() {
      const acceptedMessage = this.copilot.accept();
      this.message = acceptedMessage;
      this.setCopilotAcceptedMessage(acceptedMessage);
    },
  },
};
</script>

<template>
  <ReplyBoxBanner
    :message="message"
    :is-on-private-note="isOnPrivateNote"
    :is-personal-conversation="aceleresIsPersonalConversation"
  />
  <Teleport
    v-if="isConversationFileDragActive"
    to=".conversation-details-wrap.chat-whats"
  >
    <div
      class="acl-conversation-drop-overlay acl-conversation-drop-overlay--persistent"
      role="status"
      aria-live="polite"
    >
      <div class="acl-conversation-drop-overlay__frame">
        <span
          class="acl-conversation-drop-overlay__icon i-lucide-cloud-upload"
          aria-hidden="true"
        />
        <h4 class="acl-conversation-drop-overlay__title">
          {{ $t('CONVERSATION.REPLYBOX.DRAG_DROP') }}
        </h4>
      </div>
    </div>
  </Teleport>

  <AceleresWhatsappDisconnectedPanel
    v-if="
      (isApiInboxConnectionBlocked || whatsappInstanceUnavailable) &&
      !isOnPrivateNote
    "
    :inbox="inbox"
    :is-verifying="isApiInboxConnectionPending && !whatsappInstanceUnavailable"
    :verification-failed="
      isInboxConnectionVerificationFailed || whatsappInstanceUnconfirmed
    "
    @retry-verification="ensureInboxLoaded(inboxId)"
  />
  <!-- Aceleres: ReplyTopPanel movido pra FORA da .reply-box (pedido
       2026-07-01). Agora a pílula do reply-box só contém o editor +
       ícones. O top panel (reply/note toggle + character warning) fica
       como faixa informativa ACIMA da pílula. -->
  <ReplyTopPanel
    v-if="false"
    class="acl-reply-top-out mx-2"
    :mode="replyType"
    :conversation-id="conversationId"
    :disabled="
      (copilot.isActive.value && copilot.isButtonDisabled.value) ||
      showAudioRecorderEditor
    "
    :is-editor-disabled="isEditorDisabled"
    :is-message-length-reaching-threshold="isMessageLengthReachingThreshold"
    :characters-remaining="charactersRemaining"
    :editor-content="message"
    @set-reply-mode="setReplyMode"
    @toggle-editor-size="toggleEditorSize"
    @toggle-copilot="copilot.toggleEditor"
    @execute-copilot-action="executeCopilotAction"
  />
  <!-- Aceleres: banners informacionais movidos pra FORA da .reply-box
       (pedido 2026-07-01) — reply-to preview, quoted email, attachments
       e signature alert viram cards acima da pílula em vez de morar
       dentro do input. Assim a pílula fica exclusiva pra:
       [+] [emoji] [placeholder text] [🎤/enviar]. -->
  <!-- Aceleres: a prateleira branca cola o card de resposta na pílula do
       compositor, como no WhatsApp (os dois viram um bloco só). -->
  <div
    v-if="shouldShowReplyToMessage"
    class="acl-reply-banners mx-2"
    :class="[
      replyCardAttached
        ? 'acl-reply-banners--attached mb-0 max-md:!mr-[62px]'
        : 'mb-1',
      isPrivate && isMobileViewport
        ? 'acl-reply-banners--note-mobile max-md:!border-[#f3d298] max-md:!bg-[#fffbeb]'
        : '',
    ]"
  >
    <ReplyToMessage
      :message="inReplyTo"
      :class="isPrivate && isMobileViewport ? 'max-md:!bg-[#fff4d6]' : ''"
      @dismiss="resetReplyToMessage"
    />
  </div>
  <QuotedEmailPreview
    v-if="shouldShowQuotedPreview && isDefaultEditorMode"
    :quoted-email-text="quotedEmailText"
    :preview-text="quotedEmailPreviewText"
    class="acl-reply-banners mx-2 mb-1"
    @toggle="toggleQuotedReply"
  />
  <div
    v-if="hasAttachments && isDefaultEditorMode"
    class="acl-reply-banners mx-2 mb-1 bg-transparent py-0"
    @paste="onPaste"
  >
    <AttachmentPreview
      :attachments="attachedFiles"
      :conversation="currentChat"
      :contact="currentContact"
      :mode="showWhatsAppGallery ? 'gallery' : 'compact'"
      :enable-multiple-file-upload="enableMultipleFileUpload"
      :on-add-more="prepareMediaUpload"
      :is-sending="isSendingAttachments"
      :send-progress="attachmentSendProgress"
      @remove-attachment="removeAttachment"
      @update-caption="updateAttachmentCaption"
      @send="onSendReply"
    />
  </div>
  <div
    v-show="!showWhatsAppGallery"
    v-if="isMetaMessagingWindowExpired"
    class="acl-reply-banners mx-2 mb-1 flex items-start gap-2 rounded-[12px] border border-[#F59E0B] bg-[#FFFBEB] px-4 py-3 text-sm leading-5 text-[#92400E]"
    role="alert"
    data-testid="meta-window-expired-banner"
  >
    <span
      class="i-lucide-clock-alert mt-0.5 size-4 flex-shrink-0"
      aria-hidden="true"
    />
    <span>{{ $t('CONVERSATION.FOOTER.SOCIAL_WINDOW_EXPIRED') }}</span>
  </div>
  <div
    v-show="!showWhatsAppGallery"
    ref="replyEditor"
    class="reply-box"
    :class="[
      replyBoxClass,
      {
        'reply-box--gallery-hidden': showWhatsAppGallery,
        'reply-box--copilot-active': copilot.isActive.value,
        'max-md:before:!top-0 max-md:before:!rounded-t-none max-md:before:!border-t-0':
          replyCardAttached,
      },
    ]"
  >
    <div
      v-if="isOnPrivateNote"
      class="acl-internal-note-banner relative !border-b-0 after:absolute after:bottom-0 after:left-2 after:right-2 after:border-b after:border-n-amber-7/60 after:content-['']"
    >
      <strong class="inline-flex shrink-0 items-center gap-1 rounded-full bg-n-amber-4 px-2 py-1 text-xs font-bold uppercase tracking-normal !text-[#79580d]">
        <span class="i-ph-note-pencil size-3.5" aria-hidden="true" />
        {{ $t('CONVERSATION.REPLYBOX.INTERNAL_NOTE_BADGE') }}
      </strong>
      <span class="acl-internal-note-team-only inline-flex items-center gap-1 before:!hidden">
        <span class="i-lucide-lock-keyhole size-3" aria-hidden="true" />
        {{ $t('CONVERSATION.REPLYBOX.TEAM_ONLY') }}
      </span>
      <!-- Aceleres: o X de voltar para resposta e incondicional. Escondia-se
           quando `can_reply` era falso, prendendo o agente na nota interna. -->
      <button
        type="button"
        class="ml-auto inline-flex size-10 min-h-10 min-w-10 shrink-0 items-center justify-center rounded-full text-n-amber-11 transition-colors hover:bg-n-amber-4 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-n-amber-8"
        :aria-label="$t('CONVERSATION.REPLYBOX.BACK_TO_REPLY')"
        @click="onToggleNoteMode"
      >
        <span class="i-lucide-x size-5" />
      </button>
    </div>
    <ArticleSearchPopover
      v-if="showArticleSearchPopover && connectedPortalSlug"
      :selected-portal-slug="connectedPortalSlug"
      @insert="handleInsert"
      @close="onSearchPopoverClose"
    />
    <!-- No telemovel, a troca e imediata. O modo out-in sem animacao CSS
         remove o editor anterior antes de montar o Jarvis e quebra a troca. -->
    <Transition
      :mode="isMobileViewport ? undefined : 'out-in'"
      :css="!isMobileViewport"
      enter-active-class="transition-all duration-300 ease-out"
      enter-from-class="opacity-0 translate-y-2 scale-[0.98]"
      enter-to-class="opacity-100 translate-y-0 scale-100"
      leave-active-class="transition-all duration-200 ease-in"
      leave-from-class="opacity-100 translate-y-0 scale-100"
      leave-to-class="opacity-0 translate-y-2 scale-[0.98]"
      @after-enter="onReplyEditorTransitionAfterEnter"
    >
      <div :key="copilot.editorTransitionKey.value" class="reply-box__top">
        <!-- Aceleres: ReplyToMessage removido daqui — agora é banner
             acima do .reply-box (via .acl-reply-banners). -->
        <!-- Aceleres: no desktop o picker usa o posicionador do Floating
             Vue para permanecer ancorado ao botão de emoji do compositor.
             No mobile ele vira uma folha ancorada ao rodapé (ver
             .acl-composer-emoji-sheet no fim do template). -->
        <Dropdown
          v-if="showEmojiPicker && !isMobileViewport"
          :shown="showEmojiPicker"
          :triggers="[]"
          :popper-triggers="[]"
          :reference-node="getEmojiPickerTrigger"
          placement="top"
          :distance="8"
          :overflow-padding="8"
          flip
          shift
          shift-cross-axis
          strategy="fixed"
          compute-transform-origin
          handle-resize
          :auto-hide="false"
          popper-class="acl-composer-emoji-popper"
        >
          <span class="absolute size-0 overflow-hidden" aria-hidden="true" />
          <template #popper>
            <div
              v-on-clickaway="hideEmojiPicker"
              class="aceleres-emoji-picker-card acl-inline-picker"
              @click.stop
            >
              <div class="aceleres-emoji-picker-body">
                <EmojiInput
                  embedded
                  allow-svg-upload
                  allow-lottie-stickers
                  show-recent-reactions
                  :show-search-icon="false"
                  :on-click="addIntoEditor"
                  :on-gif-select="
                    isAFacebookInbox && !isPrivate ? null : onGifSelect
                  "
                  scroll-sync-tabs
                  progressive-categories
                />
              </div>
            </div>
          </template>
        </Dropdown>
        <ReplyEmailHead
          v-if="showReplyHead && isDefaultEditorMode"
          v-model:cc-emails="ccEmails"
          v-model:bcc-emails="bccEmails"
          v-model:to-emails="toEmails"
        />
        <AudioRecorder
          v-if="showAudioRecorderEditor"
          ref="audioRecorderInput"
          :audio-record-format="audioRecordFormat"
          variant="conversation"
          :is-paused="isRecordingPaused"
          @recorder-progress-changed="onRecordProgressChanged"
          @finish-record="onFinishRecorder"
          @play="recordingAudioState = 'playing'"
          @pause="recordingAudioState = 'paused'"
          @record-error="onRecordError"
          @delete="deleteAudioRecording"
          @playpause="toggleAudioRecorderPlayPause"
          @send="sendAudioRecording"
        />
        <CopilotEditorSection
          v-if="copilot.isActive.value && !showAudioRecorderEditor"
          :show-copilot-editor="copilot.showEditor.value"
          :is-generating-content="copilot.isGenerating.value"
          :generated-content="copilot.generatedContent.value"
          :are-actions-disabled="copilot.isButtonDisabled.value"
          :placeholder="$t('CONVERSATION.FOOTER.COPILOT_MSG_INPUT')"
          @focus="onFocus"
          @blur="onBlur"
          @clear-selection="clearEditorSelection"
          @close="closeCopilotEditor"
          @content-ready="copilot.setContentReady"
          @send="copilot.sendFollowUp"
          @accept="onSubmitCopilotReply"
          @discard="closeCopilotEditor"
        />
        <WootMessageEditor
          v-else-if="!showWhatsAppGallery && !showAudioRecorderEditor"
          ref="messageEditor"
          v-model="message"
          :conversation-id="conversationId"
          :editor-id="editorStateId"
          class="input popover-prosemirror-menu"
          :is-private="isOnPrivateNote"
          :placeholder="messagePlaceHolder"
          :update-selection-with="updateEditorSelectionWith"
          :min-height="4"
          :disabled="isEditorDisabled"
          :focus-on-mount="!suppressEditorFocusOnMount"
          enable-variables
          :variables="messageVariables"
          :signature="messageSignature"
          allow-signature
          :channel-type="channelType"
          :medium="inbox.medium"
          @typing-off="onTypingOff"
          @typing-on="onTypingOn"
          @focus="onFocus"
          @blur="onBlur"
          @toggle-user-mention="toggleUserMention"
          @toggle-canned-menu="toggleCannedMenu"
          @toggle-variables-menu="toggleVariablesMenu"
          @clear-selection="clearEditorSelection"
          @execute-copilot-action="executeCopilotAction"
          @canned-attachment="handleCannedAttachment"
          @keydown="onEditorKeydown"
          @send="onEditorSend"
        />

        <!-- Aceleres: QuotedEmailPreview e AttachmentPreview ficam acima do
             .reply-box para manter o composer limpo. -->
      </div>
    </Transition>

    <Transition
      :mode="isMobileViewport ? undefined : 'out-in'"
      :css="!isMobileViewport"
      enter-active-class="transition-all duration-300 ease-out"
      enter-from-class="opacity-0 translate-y-2 scale-[0.98]"
      enter-to-class="opacity-100 translate-y-0 scale-100"
      leave-active-class="transition-all duration-200 ease-in"
      leave-from-class="opacity-100 translate-y-0 scale-100"
      leave-to-class="opacity-0 translate-y-2 scale-[0.98]"
    >
      <div
        v-if="aceleresNeedsStartGate"
        key="aceleres-start-gate"
        class="aceleres-start-gate col-start-1 row-start-1 flex items-center justify-center p-1"
      >
        <button
          type="button"
          :disabled="aceleresStarting"
          class="aceleres-start-btn inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-[#00a884] text-white text-sm font-semibold hover:bg-[#008f6f] disabled:opacity-60 transition"
          @click="aceleresStartConversation"
        >
          <span class="i-lucide-play size-4" />
          {{ aceleresStarting ? 'Iniciando…' : 'Iniciar atendimento' }}
        </button>
      </div>
      <CopilotReplyBottomPanel
        v-else-if="copilot.isActive.value && !isCopilotMobileViewport"
        key="copilot-bottom-panel"
        :is-generating-content="copilot.isButtonDisabled.value"
        @submit="onSubmitCopilotReply"
        @cancel="copilot.reset"
      />
      <ReplyBottomPanel
        v-else-if="
          !copilot.isActive.value &&
          !showAudioRecorderEditor &&
          !showWhatsAppGallery
        "
        key="reply-bottom-panel"
        :conversation-id="conversationId"
        :enable-multiple-file-upload="enableMultipleFileUpload"
        :enable-whats-app-templates="showWhatsappTemplates"
        :enable-content-templates="showContentTemplates"
        :inbox="inbox"
        :is-on-private-note="isOnPrivateNote"
        :is-recording-audio="isRecordingAudio"
        :is-send-disabled="isReplyButtonDisabled"
        :is-note="isPrivate"
        :is-editor-disabled="isEditorDisabled"
        :on-file-upload="prepareMediaUpload"
        :on-open-media="openMediaPicker"
        :on-send="onSendReply"
        :conversation-type="conversationType"
        :recording-audio-duration-text="recordingAudioDurationText"
        :recording-audio-state="recordingAudioState"
        :send-button-text="replyButtonLabel"
        :show-copilot-button="!copilot.isActive.value"
        :show-audio-recorder="showAudioRecorder"
        :show-emoji-picker="showEmojiPicker"
        :is-mobile-viewport="isMobileViewport"
        :show-file-upload="showFileUpload"
        :show-quoted-reply-toggle="shouldShowQuotedReplyToggle"
        :quoted-reply-enabled="quotedReplyPreference"
        :toggle-audio-recorder-play-pause="toggleAudioRecorderPlayPause"
        :toggle-audio-recorder="toggleAudioRecorder"
        :finish-audio-recording="finishAudioRecording"
        :delete-audio-recording="deleteAudioRecording"
        :is-recording-paused="isRecordingPaused"
        :toggle-emoji-picker="toggleEmojiPicker"
        :message="message"
        :portal-slug="connectedPortalSlug"
        :new-conversation-modal-active="newConversationModalActive"
        @select-whatsapp-template="openWhatsappTemplateModal"
        @toggle-canned-menu="toggleAceleresCannedPopover"
        @execute-copilot-action="executeCopilotAction"
        @select-content-template="openContentTemplateModal"
        @toggle-insert-article="toggleInsertArticle"
        @toggle-quoted-reply="toggleQuotedReply"
        @toggle-note-mode="onToggleNoteMode"
        @select-contact="openSendContactModal"
      />
    </Transition>

    <SendContactModal
      v-model:show="showSendContactModal"
      @send="onSendContactCard"
    />

    <AceleresCannedPopover
      :show="showAceleresCannedPopover"
      :account-id="accountId"
      @close="closeAceleresCannedPopover"
      @insert="onAceleresCannedInsert"
    />

    <WhatsappTemplates
      :inbox-id="inbox.id"
      :show="showWhatsAppTemplatesModal"
      @close="hideWhatsappTemplatesModal"
      @on-send="onSendWhatsAppReply"
      @cancel="hideWhatsappTemplatesModal"
    />

    <ContentTemplates
      :inbox-id="inbox.id"
      :show="showContentTemplatesModal"
      @close="hideContentTemplatesModal"
      @on-send="onSendContentTemplateReply"
      @cancel="hideContentTemplatesModal"
    />

    <woot-confirm-modal
      ref="confirmDialog"
      :title="$t('CONVERSATION.REPLYBOX.UNDEFINED_VARIABLES.TITLE')"
      :description="undefinedVariableMessage"
    />

    <MobileEmojiSheet
      :open="!!reactionSheetRequest"
      :on-select="onReactionSheetSelect"
      @close="reactionSheetRequest = null"
    />
  </div>

  <!-- Aceleres: no mobile o picker deixa de flutuar por cima da conversa e
       passa a ser uma folha no fluxo, logo abaixo do compositor. Como a
       lista de mensagens é flex-1, abrir a folha empurra a conversa para
       cima e o painel sobe de baixo, igual ao WhatsApp. O card reaproveita
       as mesmas classes do picker do desktop; só o modificador
       .acl-composer-emoji-sheet ajusta largura, altura e cantos. -->
  <Transition
    enter-active-class="acl-composer-emoji-sheet-enter"
    leave-active-class="acl-composer-emoji-sheet-leave"
    enter-from-class="acl-composer-emoji-sheet-hidden"
    leave-to-class="acl-composer-emoji-sheet-hidden"
  >
    <div
      v-if="showEmojiPicker && isMobileViewport"
      ref="emojiSheet"
      v-on-clickaway="onEmojiSheetClickAway"
      class="aceleres-emoji-picker-card acl-inline-picker acl-composer-emoji-sheet"
      :class="{ 'acl-composer-emoji-sheet--dragging': emojiSheetDragging }"
      :style="emojiSheetStyle"
      role="dialog"
      :aria-label="$t('CONVERSATION.REPLYBOX.TIP_EMOJI_ICON')"
      @click.stop
    >
      <!-- Puxador: arrastar para cima cresce a folha, para baixo encolhe e
           soltar lá embaixo fecha. Só ele é arrastável — a grade de emojis
           precisa continuar rolando com o dedo. -->
      <div
        class="acl-composer-emoji-sheet__handle"
        aria-hidden="true"
        @pointerdown="onEmojiSheetDragStart"
        @pointermove="onEmojiSheetDragMove"
        @pointerup="onEmojiSheetDragEnd"
        @pointercancel="onEmojiSheetDragEnd"
      >
        <span class="acl-composer-emoji-sheet__grabber" />
      </div>
      <div class="aceleres-emoji-picker-body">
        <!-- Aceleres 2026-08-18: `focus-search-on-mount` FALSO aqui. O padrao do
             EmojiInput e `true`, e ao montar ele faz `focus()` no campo de
             busca — num telemovel isso levanta o teclado virtual, que era o
             relato: tocar na carita abria o seletor E o teclado, os dois a
             disputar o ecra. O `blur()` que o `toggleEmojiPicker` ja faz nao
             chegava, porque corre ANTES de o seletor montar e o foco vinha
             depois. Esta instancia so existe no telemovel (`v-if` com
             `isMobileViewport`); a do computador e outra e mantem o foco
             automatico, que ali e util para filtrar a escrever. -->
        <EmojiInput
          embedded
          show-recent-reactions
          :show-search-icon="false"
          :focus-search-on-mount="false"
          mobile-sheet
          :on-backspace="onEmojiSheetBackspace"
          :on-click="addIntoEditor"
          :on-gif-select="isAFacebookInbox && !isPrivate ? null : onGifSelect"
          scroll-sync-tabs
          progressive-categories
        />
      </div>
    </div>
  </Transition>
</template>

<style lang="scss" scoped>
.send-button {
  @apply mb-0;
}

.reply-box {
  @apply relative mb-2 mx-2 border border-n-weak bg-n-solid-1;
  border-radius: 8px;

  &.is-private {
    @apply bg-n-solid-amber dark:border-n-amber-3/10 border-n-amber-12/5;

    :deep(.right-wrap > .wa-action-btn--send.acl-internal-note-send) {
      @apply bg-n-amber-9 text-white !important;
    }

    :deep(
      .right-wrap > .wa-action-btn--send.acl-internal-note-send:hover:not(:disabled)
    ) {
      @apply bg-n-amber-10 text-white !important;
    }

    :deep(
      .right-wrap > .wa-action-btn--send.acl-internal-note-send:focus-visible
    ) {
      @apply outline outline-2 outline-offset-2 outline-n-amber-9 !important;
    }
  }
}

.reply-box.reply-box--gallery-hidden {
  display: none !important;
}

/* Aceleres: com uma resposta ativa, a prateleira do card e a pílula do
   compositor viram um retângulo branco único, como no WhatsApp: a borda
   entre os dois some e o raio passa a ser só nas quatro pontas externas.
   O `!important` na pílula é obrigatório — `public/aceleres-overrides.css`
   carrega depois do Vite e fixa borda, raio e sombra em `div.reply-box`. */
.acl-reply-banners--attached {
  padding: 8px 8px 4px;
  border: 1px solid rgba(0, 0, 0, 0.18);
  border-bottom: 0;
  border-radius: 16px 16px 0 0;
  background: #ffffff;
}

:global(.dark) .acl-reply-banners--attached {
  border-color: rgba(255, 255, 255, 0.18);
  background: #2c2c2c;
}

.reply-box.reply-box--with-reply {
  margin-top: 0 !important;
  border-top: 0 !important;
  border-radius: 0 0 26px 26px !important;
  box-shadow: none !important;
}

.reply-box.reply-box--multi-line {
  overflow: hidden;
}

.send-button {
  @apply mb-0;
}

.reply-box__top {
  @apply relative px-3 pt-0.5 pb-0 flex flex-col items-stretch;
}

/* Aceleres: o parágrafo do ProseMirror vem do Editor.vue (componente filho),
   por isso o :deep — sem ele o atributo do scoped não alcança o nó. */
.reply-box__top :deep(.ProseMirror p) {
  font-size: 14px;
}

/* Aceleres: o CSS global do compositor fixa o placeholder em uma linha com
   reticências. Na nota interna, o texto quebra e participa do fluxo do editor
   para que a altura acompanhe todas as linhas em viewports estreitos. */
.reply-box.is-private
  .reply-box__top
  :deep(.ProseMirror p.empty-node[data-placeholder]:first-child::before) {
  display: block !important;
  width: 100% !important;
  max-width: 100% !important;
  overflow: visible !important;
  white-space: normal !important;
  text-overflow: clip !important;
  overflow-wrap: anywhere !important;
}

@media (max-width: 767px) {
  .reply-box.is-private
    .reply-box__top
    :deep(.ProseMirror:has(> p.empty-node[data-placeholder]:only-child)) {
    min-height: 44px !important;
    height: auto !important;
    overflow: visible !important;
  }

  .reply-box.is-private
    .reply-box__top
    :deep(.ProseMirror p.empty-node[data-placeholder]:only-child) {
    min-height: 22px !important;
    line-height: 22px !important;
    margin: 0 !important;
  }

  .reply-box.is-private
    .reply-box__top
    :deep(.ProseMirror p.empty-node[data-placeholder]:only-child::before) {
    position: static !important;
    float: left !important;
    height: 0 !important;
  }
}

.reply-box:not(.reply-box--copilot-active)
  .reply-box__top
  :deep(.ProseMirror-woot-style) {
  height: auto;
  max-height: 8lh;
}

.emoji-dialog {
  @apply top-[unset] -bottom-10 ltr:-left-80 ltr:right-[unset] rtl:left-[unset] rtl:-right-80;

  &::before {
    filter: drop-shadow(0px 4px 4px rgba(0, 0, 0, 0.08));
    @apply ltr:-right-4 bottom-2 rtl:-left-4 ltr:rotate-[270deg] rtl:rotate-[90deg];
  }
}

.emoji-dialog--expanded {
  @apply left-[unset] bottom-0 absolute z-[100];

  &::before {
    transform: rotate(0deg);
    @apply ltr:left-1 rtl:right-1 -bottom-2;
  }
}

:global(.acl-composer-emoji-popper) {
  z-index: 2147483647 !important;
}

:global(.acl-composer-emoji-popper.v-popper__popper.v-popper__popper--shown) {
  @apply transition-opacity duration-200 ease-[cubic-bezier(0.22,1,0.36,1)];
}

:global(.acl-composer-emoji-popper .v-popper__wrapper) {
  @apply translate-y-0 scale-100 transition-transform duration-200 ease-[cubic-bezier(0.22,1,0.36,1)];
}

:global(.acl-composer-emoji-popper.v-popper__popper--show-from .v-popper__wrapper) {
  @apply translate-y-1 scale-0;
}

@media (prefers-reduced-motion: reduce) {
  :global(.acl-composer-emoji-popper.v-popper__popper.v-popper__popper--shown) {
    @apply transition-none;
  }

  :global(.acl-composer-emoji-popper .v-popper__wrapper) {
    @apply transition-none;
  }

  :global(.acl-composer-emoji-popper.v-popper__popper--show-from .v-popper__wrapper) {
    @apply translate-y-0 scale-100;
  }
}

:global(.acl-composer-emoji-popper .v-popper__inner) {
  background: transparent !important;
  border: 0 !important;
  box-shadow: none !important;
}

:global(.acl-composer-emoji-popper .v-popper__arrow-container) {
  display: none !important;
}

:global(.aceleres-emoji-picker-card.acl-inline-picker) {
  width: min(420px, calc(100vw - 16px)) !important;
  max-width: calc(100vw - 16px) !important;
  height: min(420px, calc(100dvh - 32px)) !important;
  max-height: min(420px, calc(100dvh - 32px)) !important;
  background: #f7f8fa;
  border: 1px solid rgba(0, 0, 0, 0.06);
  border-radius: 12px;
  box-shadow:
    0 12px 28px rgba(0, 0, 0, 0.2),
    0 2px 4px rgba(0, 0, 0, 0.06);
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

:global(.aceleres-emoji-picker-card .aceleres-emoji-picker-body) {
  width: 100%;
  height: 100%;
  overflow: hidden;
}

:global(.aceleres-emoji-picker-card .emoji-dialog) {
  position: static !important;
  width: 100% !important;
  height: 100% !important;
  max-height: none !important;
  box-shadow: none !important;
  outline: none !important;
  border: 0 !important;
  border-radius: 0 !important;
}

:global(.aceleres-emoji-picker-card .emoji-dialog::before) {
  display: none !important;
}

:global(.aceleres-emoji-picker-card .emoji-dialog > .flex.flex-col) {
  height: 100% !important;
}

:global(.aceleres-emoji-picker-card .emoji-dialog--footer) {
  background: #f7f8fa !important;
  border-bottom: 0 !important;
  padding: 10px 14px !important;
}

:global(.aceleres-emoji-picker-card .emoji-dialog--footer ul) {
  gap: 0 !important;
  justify-content: space-between;
  overflow: hidden !important;
  left: 0 !important;
  padding: 2px 0 !important;
}

:global(.aceleres-emoji-picker-card .emoji-dialog--footer li) {
  flex: 1 1 0;
  position: relative;
  padding: 2px !important;
}

:global(.aceleres-emoji-picker-card .emoji-dialog--footer li .emoji--item) {
  width: 100% !important;
  height: 34px !important;
  color: rgba(0, 0, 0, 0.5);
  opacity: 0.65;
}

:global(.aceleres-emoji-picker-card .emoji-dialog--footer li .active) {
  background: transparent !important;
  color: rgba(0, 0, 0, 0.95);
  font-weight: 700;
  opacity: 1 !important;
}

:global(.aceleres-emoji-picker-card .emoji-item) {
  flex: 1 1 0%;
  height: auto !important;
  min-height: 0;
}

:global(.dark .aceleres-emoji-picker-card.acl-inline-picker) {
  background: #1e293b;
}

/* Aceleres: variante mobile do picker. Reaproveita todas as regras acima e
   só troca o que muda numa folha ancorada ao rodapé: largura total, altura
   proporcional à tela, cantos arredondados apenas em cima e sombra jogada
   para cima. Como ela vive no fluxo (logo abaixo da pílula do compositor),
   abrir empurra a lista de mensagens para cima em vez de cobri-la. */
.acl-composer-emoji-sheet {
  /* A altura padrão vive numa variável para o arrasto poder sobrescrevê-la por
     estilo inline sem repetir o valor — a regra base do card usa !important,
     então um `height` inline sozinho perderia. */
  --acl-emoji-sheet-default-height: clamp(15rem, 45dvh, 22.5rem);

  flex: none;
  margin-top: 0.5rem;
  width: 100% !important;
  max-width: 100% !important;
  height: var(
    --acl-emoji-sheet-height,
    var(--acl-emoji-sheet-default-height)
  ) !important;
  max-height: var(
    --acl-emoji-sheet-height,
    var(--acl-emoji-sheet-default-height)
  ) !important;
  border-radius: 12px 12px 0 0;
  border-left-width: 0;
  border-right-width: 0;
  border-bottom-width: 0;
  box-shadow: 0 -6px 18px rgba(0, 0, 0, 0.12);
  padding-bottom: env(safe-area-inset-bottom, 0px);
  /* O encaixe de volta ao mínimo é animado; durante o arrasto a altura tem de
     seguir o dedo sem atraso. */
  transition: height 160ms ease;
}

.acl-composer-emoji-sheet--dragging {
  transition: none;
}

/* O puxador cede a altura ao corpo; sem isto o `height: 100%` herdado do card
   somaria à barra e a grade estouraria a folha. */
.acl-composer-emoji-sheet .aceleres-emoji-picker-body {
  flex: 1;
  min-height: 0;
  height: auto;
}

.acl-composer-emoji-sheet__handle {
  /* Sem isto o navegador rola a página em vez de deixar o gesto chegar ao
     pointermove. */
  touch-action: none;
  cursor: grab;
  display: flex;
  flex: none;
  align-items: center;
  justify-content: center;
  /* O alvo tinha 16px de altura — o risco do traço mais o padding. O arrasto
     funcionava, mas era preciso acertar uma faixa de 16px com o dedo, e quase
     todo toque caía no conteúdo abaixo: na prática a folha não subia. 2.25rem
     dá uma faixa utilizável sem mexer no traço, que continua do mesmo tamanho.
     Esta folha só é montada no telemóvel (`isMobileViewport`), então o
     computador não vê nada disto. */
  min-height: 2.25rem;
  padding: 0.5rem 0 0.25rem;
}

.acl-composer-emoji-sheet--dragging .acl-composer-emoji-sheet__handle {
  cursor: grabbing;
}

.acl-composer-emoji-sheet__grabber {
  width: 2.25rem;
  height: 0.25rem;
  border-radius: 9999px;
  background: rgba(0, 0, 0, 0.2);
}

:global(.dark .acl-composer-emoji-sheet__grabber) {
  background: rgba(255, 255, 255, 0.28);
}

/* Sobe de baixo para cima, como o painel de emojis do WhatsApp. */
.acl-composer-emoji-sheet-hidden {
  transform: translateY(100%);
}

.acl-composer-emoji-sheet-enter {
  transition: transform 220ms cubic-bezier(0.22, 1, 0.36, 1);
  will-change: transform;
}

.acl-composer-emoji-sheet-leave {
  transition: transform 160ms cubic-bezier(0.4, 0, 1, 1);
  will-change: transform;
}
</style>
